import { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Satellite, MapPin, Wind, Droplets, Target, AlertTriangle, Moon } from "lucide-react";

// ============================================================================
// Space-Age Control Deck. All surfaces gated on `user.space_age_deck_enabled`
// (guarded by <SpaceAgeControlDeck>). Deck extras persisted on the user record:
//   space_age_deck_density         · number of commodities in the ticker (2..12)
//   space_age_cursor_enabled       · custom cyber-ring cursor mode
//   space_age_weather_min/_max     · threshold pair for the dispatch toast
//   space_age_pinned_commodities   · list of ticker codes pinned above the grid
// ============================================================================

// Streaming Lumber / Copper industrial commodity ticker. Ticks the price by a
// tiny random walk every 3s so the marquee reads as live even in the preview.
const COMMODITY_SEEDS = [
  { code: "LMBR", label: "Lumber",       unit: "$/1kbf",      base: 570.50 },
  { code: "HG",   label: "Copper",       unit: "$/lb",        base:   6.66 },
  { code: "HRC",  label: "Steel HRC",    unit: "$/short-ton", base: 748.00 },
  { code: "ULSD", label: "Diesel ULSD",  unit: "$/gal",       base:   2.87 },
  { code: "CL",   label: "WTI Crude",    unit: "$/bbl",       base:  74.15 },
  { code: "NG",   label: "Natural Gas",  unit: "$/MMBtu",     base:   2.94 },
  { code: "AL",   label: "Aluminum",     unit: "$/ton",       base: 2214.00 },
  { code: "NI",   label: "Nickel",       unit: "$/ton",       base: 17800.00 },
  { code: "ZN",   label: "Zinc",         unit: "$/ton",       base: 2610.00 },
  { code: "GC",   label: "Gold",         unit: "$/oz",        base: 2318.00 },
  { code: "SI",   label: "Silver",       unit: "$/oz",        base:  29.42 },
  { code: "ZC",   label: "Corn",         unit: "$/bu",        base:   4.18 },
];

const formatPrice = (v) => {
  const n = Number(v);
  if (n >= 1000) return n.toLocaleString(undefined, { maximumFractionDigits: 0 });
  if (n >= 10)   return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return n.toFixed(3);
};

// ── Moon Phase Cycle — real synodic-month math (zero hardcoded calendar
// tables). SYNODIC_MONTH_DAYS is the average new-moon-to-new-moon interval;
// KNOWN_NEW_MOON_MS anchors one verified historical new moon so every phase
// going forward or backward derives purely from elapsed days. ──────────────
const SYNODIC_MONTH_DAYS = 29.530588853;
const KNOWN_NEW_MOON_MS = new Date("2000-01-06T18:14:00Z").getTime();
// Returns 0..1 — fraction of the current synodic month elapsed (0 = new
// moon, 0.5 = full moon, 1 → wraps back to new moon).
const getMoonPhaseFraction = (date) => {
  const diffDays = (date.getTime() - KNOWN_NEW_MOON_MS) / 86400000;
  const cycles = diffDays / SYNODIC_MONTH_DAYS;
  return cycles - Math.floor(cycles);
};
// Returns 0..1 illumination — 0 at new moon (dark), 1 at full moon (bright).
// Standard cosine approximation of the lit-fraction curve across the cycle.
const getMoonIllumination = (phaseFraction) => (1 - Math.cos(2 * Math.PI * phaseFraction)) / 2;
// Human-readable phase name for the lunar orb's tooltip.
const getMoonPhaseName = (phaseFraction) => {
  if (phaseFraction < 0.03 || phaseFraction > 0.97) return "New Moon";
  if (phaseFraction < 0.22) return "Waxing Crescent";
  if (phaseFraction < 0.28) return "First Quarter";
  if (phaseFraction < 0.47) return "Waxing Gibbous";
  if (phaseFraction < 0.53) return "Full Moon";
  if (phaseFraction < 0.72) return "Waning Gibbous";
  if (phaseFraction < 0.78) return "Last Quarter";
  return "Waning Crescent";
};

export function CommodityTicker() {
  const { user, checkAuth } = useAuth();
  const density = Math.max(2, Math.min(12, Number(user?.space_age_deck_density) || 6));
  const pinned = Array.isArray(user?.space_age_pinned_commodities) ? user.space_age_pinned_commodities : [];
  const active = useMemo(() => COMMODITY_SEEDS.slice(0, density), [density]);
  const [ticks, setTicks] = useState(() => active.map((c) => ({ ...c, price: c.base, delta: 0 })));
  const [tickerSource, setTickerSource] = useState("local");
  const [watchlistOpen, setWatchlistOpen] = useState(false);
  const [watchlist, setWatchlist] = useState({}); // { [code]: { ceiling: "", floor: "" } }
  const [alertHistory, setAlertHistory] = useState([]); // [{id, code, label, type, price, threshold, ts}]
  const serverTickRef = useRef(null);
  const watchlistRef = useRef({});
  const firedAlertsRef = useRef(new Set());

  // Keep ref in sync with watchlist state (avoids stale closure in poll callback)
  useEffect(() => { watchlistRef.current = watchlist; }, [watchlist]);

  // Reset tick set when density changes
  useEffect(() => {
    setTicks(active.map((c) => ({ ...c, price: c.base, delta: 0 })));
  }, [active]);

  // Poll backend every 30s for authoritative server ticks + watchlist alert checks
  useEffect(() => {
    let cancelled = false;
    const fetchServerTicks = async () => {
      try {
        const { data } = await api.get("/v1/commodity-prices");
        if (cancelled) return;
        if (Array.isArray(data?.commodities) && data.commodities.length > 0) {
          serverTickRef.current = data.commodities;
          setTickerSource(data.source === "live" ? "live" : "server");
          setTicks((prev) =>
            prev.map((c) => {
              const srv = data.commodities.find((s) => s.code === c.code);
              return srv ? { ...c, price: srv.price, delta: srv.delta } : c;
            })
          );
          // ---- Watchlist alert check ----
          data.commodities.forEach((c) => {
            const alert = watchlistRef.current[c.code];
            if (!alert) return;
            const ceiling = Number(alert.ceiling);
            const floor = Number(alert.floor);
            if (alert.ceiling && !isNaN(ceiling) && c.price > ceiling) {
              const key = `${c.code}-ceil-${Math.floor(c.price)}`;
              if (!firedAlertsRef.current.has(key)) {
                firedAlertsRef.current.add(key);
                const ts = new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
                setAlertHistory((prev) => [{
                  id: key, code: c.code, label: c.label,
                  type: "ceiling", price: c.price, threshold: ceiling, ts,
                }, ...prev].slice(0, 50));
                toast.warning(
                  `▲ ${c.label} above ceiling — $${formatPrice(c.price)} > $${ceiling}`,
                  { duration: 6000, id: key }
                );
              }
            }
            if (alert.floor && !isNaN(floor) && c.price < floor) {
              const key = `${c.code}-flr-${Math.floor(c.price)}`;
              if (!firedAlertsRef.current.has(key)) {
                firedAlertsRef.current.add(key);
                const ts = new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
                setAlertHistory((prev) => [{
                  id: key, code: c.code, label: c.label,
                  type: "floor", price: c.price, threshold: floor, ts,
                }, ...prev].slice(0, 50));
                toast.warning(
                  `▼ ${c.label} below floor — $${formatPrice(c.price)} < $${floor}`,
                  { duration: 6000, id: key }
                );
              }
            }
          });
        }
      } catch {
        // Network error — keep last known local walk
      }
    };
    fetchServerTicks();
    const pollId = setInterval(fetchServerTicks, 30000);
    return () => { cancelled = true; clearInterval(pollId); };
  }, []);

  // Fast 3s client-side interpolation between server ticks for smooth motion
  useEffect(() => {
    const id = setInterval(() => {
      setTicks((prev) => prev.map((c) => {
        const drift = (Math.random() - 0.5) * 0.002 * c.base;
        const next = Math.max(c.base * 0.97, Math.min(c.base * 1.03, c.price + drift));
        return { ...c, price: next, delta: next - c.base };
      }));
    }, 3000);
    return () => clearInterval(id);
  }, []);

  const pinCommodity = async (code) => {
    const nextSet = new Set(pinned);
    if (nextSet.has(code)) nextSet.delete(code);
    else nextSet.add(code);
    const nextArr = Array.from(nextSet).slice(0, 6);
    try {
      await api.patch("/auth/space-age-prefs", { pinned_commodities: nextArr });
      await checkAuth();
      toast.success(pinned.includes(code)
        ? `Unpinned · ${code}`
        : `🛸 Pinned · ${code} tile locked above the ledger`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Pin failed");
    }
  };

  const setWatchlistField = (code, field, value) => {
    setWatchlist((prev) => ({
      ...prev,
      [code]: { ...prev[code], [field]: value },
    }));
    // Clear fired alerts for this code so new threshold triggers fresh
    firedAlertsRef.current = new Set(
      [...firedAlertsRef.current].filter((k) => !k.startsWith(code))
    );
  };

  const row = useMemo(() => [...ticks, ...ticks], [ticks]);
  const sourceLabel = tickerSource === "live" ? "● LIVE" : tickerSource === "server" ? "● SERVER" : "● LOCAL";
  const sourceColor = tickerSource === "live" ? "#34d399" : tickerSource === "server" ? "#67e8f9" : "#94a3b8";
  const activeAlertCount = Object.values(watchlist).filter((a) => a?.ceiling || a?.floor).length;

  return (
    <>
      {/* Watchlist Alert Configuration Panel (slides up above ticker) */}
      {watchlistOpen && (
        <div
          className="fixed bottom-9 left-0 right-0 z-39 border-t border-violet-500/30"
          style={{
            background: "linear-gradient(180deg, rgba(4,2,20,0.97) 0%, rgba(8,4,28,0.99) 100%)",
            backdropFilter: "blur(18px)",
            WebkitBackdropFilter: "blur(18px)",
            boxShadow: "0 -4px 24px -8px rgba(139,92,246,0.4)",
            maxHeight: "320px",
            overflowY: "auto",
          }}
          data-testid="watchlist-panel"
        >
          <div className="px-4 py-2 flex items-center justify-between border-b border-violet-500/15 sticky top-0"
            style={{ background: "rgba(8,4,28,0.99)" }}>
            <span className="text-[9px] font-mono uppercase tracking-[0.28em] text-violet-300">
              Price Threshold Alerts · {activeAlertCount} active
            </span>
            <button
              type="button"
              onClick={() => setWatchlistOpen(false)}
              data-testid="watchlist-close"
              className="text-slate-500 hover:text-slate-300 text-[10px] font-mono uppercase"
            >
              ✕ close
            </button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 p-3">
            {COMMODITY_SEEDS.slice(0, density).map((c) => {
              const alert = watchlist[c.code] || {};
              const hasAlert = alert.ceiling || alert.floor;
              return (
                <div
                  key={c.code}
                  className="rounded-xl p-2.5"
                  style={{
                    background: hasAlert ? "rgba(139,92,246,0.1)" : "rgba(255,255,255,0.03)",
                    border: `1px solid ${hasAlert ? "rgba(139,92,246,0.3)" : "rgba(255,255,255,0.06)"}`,
                  }}
                  data-testid={`watchlist-${c.code.toLowerCase()}`}
                >
                  <div className="text-[9px] font-mono text-cyan-300 tracking-widest mb-1.5">
                    {c.code} · {c.label}
                  </div>
                  <div className="space-y-1.5">
                    <div>
                      <div className="text-[8px] text-slate-500 font-mono mb-0.5">CEILING ▲</div>
                      <input
                        type="number"
                        placeholder={`> ${formatPrice(c.base)}`}
                        value={alert.ceiling || ""}
                        onChange={(e) => setWatchlistField(c.code, "ceiling", e.target.value)}
                        data-testid={`watchlist-ceiling-${c.code.toLowerCase()}`}
                        className="w-full px-2 py-1 rounded-md text-[10px] font-mono text-emerald-300 placeholder-slate-700 outline-none"
                        style={{ background: "rgba(16,185,129,0.06)", border: "1px solid rgba(16,185,129,0.2)" }}
                      />
                    </div>
                    <div>
                      <div className="text-[8px] text-slate-500 font-mono mb-0.5">FLOOR ▼</div>
                      <input
                        type="number"
                        placeholder={`< ${formatPrice(c.base)}`}
                        value={alert.floor || ""}
                        onChange={(e) => setWatchlistField(c.code, "floor", e.target.value)}
                        data-testid={`watchlist-floor-${c.code.toLowerCase()}`}
                        className="w-full px-2 py-1 rounded-md text-[10px] font-mono text-red-300 placeholder-slate-700 outline-none"
                        style={{ background: "rgba(239,68,68,0.06)", border: "1px solid rgba(239,68,68,0.18)" }}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Alert History Log */}
          <div
            className="border-t mx-3 mb-3"
            style={{ borderColor: "rgba(139,92,246,0.18)" }}
            data-testid="alert-history-section"
          >
            <div className="flex items-center justify-between py-2">
              <span className="text-[9px] font-mono uppercase tracking-[0.28em] text-violet-400">
                Breach Log · {alertHistory.length} event{alertHistory.length !== 1 ? "s" : ""}
              </span>
              {alertHistory.length > 0 && (
                <button
                  type="button"
                  onClick={() => {
                    setAlertHistory([]);
                    firedAlertsRef.current = new Set();
                  }}
                  data-testid="alert-history-clear"
                  className="text-[9px] font-mono text-slate-600 hover:text-slate-400 uppercase tracking-widest transition-colors"
                >
                  Clear log
                </button>
              )}
            </div>
            {alertHistory.length === 0 ? (
              <div className="py-3 text-center text-[10px] font-mono text-slate-600 tracking-widest">
                No breaches recorded yet — thresholds fire on the next 30s price poll
              </div>
            ) : (
              <div
                className="rounded-xl overflow-hidden"
                style={{ border: "1px solid rgba(139,92,246,0.15)", maxHeight: "160px", overflowY: "auto" }}
                data-testid="alert-history-log"
              >
                {alertHistory.map((entry) => (
                  <div
                    key={entry.id}
                    className="flex items-center gap-2 px-3 py-2 border-b last:border-b-0 hover:bg-white/[0.015] transition-colors"
                    style={{ borderColor: "rgba(139,92,246,0.08)" }}
                    data-testid={`alert-entry-${entry.id}`}
                  >
                    <span
                      className="text-[10px] font-mono font-bold flex-shrink-0"
                      style={{ color: entry.type === "ceiling" ? "#34d399" : "#f87171" }}
                    >
                      {entry.type === "ceiling" ? "▲" : "▼"}
                    </span>
                    <span className="text-[9px] font-mono text-cyan-300 flex-shrink-0 w-8">{entry.code}</span>
                    <span className="text-[9px] font-mono text-slate-400 flex-1 truncate">
                      {entry.label} {entry.type === "ceiling" ? "above" : "below"} ${entry.threshold}
                    </span>
                    <span className="text-[10px] font-mono font-bold flex-shrink-0"
                      style={{ color: entry.type === "ceiling" ? "#34d399" : "#f87171" }}>
                      ${formatPrice(entry.price)}
                    </span>
                    <span className="text-[9px] font-mono text-slate-600 flex-shrink-0">{entry.ts}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Ticker bar */}
      <div
        className="fixed bottom-0 left-0 right-0 z-40 border-t border-cyan-500/30 overflow-hidden"
        style={{
          background: "linear-gradient(180deg, rgba(2,6,23,0.90), rgba(6,25,55,0.95))",
          backdropFilter: "blur(16px)",
          WebkitBackdropFilter: "blur(16px)",
          boxShadow: "0 -8px 24px -12px rgba(6,182,212,0.55)",
        }}
        data-testid="space-age-commodity-ticker"
        data-density={density}
      >
        <div className="relative flex items-center h-9">
          <span
            className="shrink-0 px-3 h-full flex items-center gap-1.5 text-[9px] font-mono uppercase tracking-[0.28em] text-cyan-300 border-r border-cyan-500/30"
            style={{ background: "rgba(6,182,212,0.10)", textShadow: "0 0 8px rgba(6,182,212,0.65)" }}
            data-testid="space-age-ticker-source"
          >
            🛸 Industrial Feed · {density}
            <span className="text-[8px] ml-1" style={{ color: sourceColor }}>{sourceLabel}</span>
          </span>
          <div className="relative flex-1 overflow-hidden">
            <div className="flex items-center gap-8 whitespace-nowrap py-1 px-4 animate-[ticker-scroll_45s_linear_infinite]">
              {row.map((c, i) => {
                const up = c.delta >= 0;
                const isPinned = pinned.includes(c.code);
                return (
                  <button
                    key={`${c.code}-${i}`}
                    type="button"
                    onClick={() => pinCommodity(c.code)}
                    className="inline-flex items-center gap-1.5 text-[10px] font-mono hover:brightness-125 transition"
                    data-testid={`space-age-ticker-${c.code.toLowerCase()}`}
                    data-pinned={isPinned ? "true" : "false"}
                    title={isPinned ? "Click to unpin" : "Click to pin above the ledger"}
                  >
                    <span className={isPinned ? "text-cyan-300" : "text-slate-400"} style={{ letterSpacing: "0.15em" }}>
                      {isPinned ? "📌" : ""}{c.code}
                    </span>
                    <span className="text-slate-100">{c.label}</span>
                    <span className="text-white font-bold tabular-nums">${formatPrice(c.price)}</span>
                    <span className="text-[8px] text-slate-500">{c.unit}</span>
                    <span
                      className="tabular-nums text-[9px] px-1.5 rounded-md"
                      style={{
                        background: up ? "rgba(16,185,129,0.14)" : "rgba(244,63,94,0.14)",
                        color: up ? "#6ee7b7" : "#fda4af",
                        border: `1px solid ${up ? "rgba(16,185,129,0.35)" : "rgba(244,63,94,0.35)"}`,
                      }}
                    >
                      {up ? "▲" : "▼"} {Math.abs(c.delta).toFixed(2)}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
          {/* Alerts button */}
          <button
            type="button"
            onClick={() => setWatchlistOpen((o) => !o)}
            data-testid="watchlist-toggle-btn"
            className="shrink-0 px-3 h-full flex items-center gap-1.5 text-[9px] font-mono uppercase tracking-widest border-l border-cyan-500/30 transition-colors hover:bg-violet-500/10"
            style={
              activeAlertCount > 0
                ? { color: "#c4b5fd", background: "rgba(139,92,246,0.1)" }
                : { color: "#64748b" }
            }
            title="Configure price threshold alerts"
          >
            🔔
            {activeAlertCount > 0 && (
              <span className="text-[9px] font-mono text-violet-300">{activeAlertCount}</span>
            )}
          </button>
        </div>
        <style>{`
          @keyframes ticker-scroll {
            0%   { transform: translateX(0%); }
            100% { transform: translateX(-50%); }
          }
          @media (prefers-reduced-motion: reduce) {
            [data-testid="space-age-commodity-ticker"] .animate-\\[ticker-scroll_45s_linear_infinite\\] {
              animation: none !important;
            }
          }
        `}</style>
      </div>
    </>
  );
}

// Pinned-Commodity Tile Strip — persistent high-contrast tiles resting above
// the ledger grid. Uses the same random-walk logic as the marquee.
export function PinnedCommodityStrip() {
  const { user } = useAuth();
  const pins = Array.isArray(user?.space_age_pinned_commodities) ? user.space_age_pinned_commodities : [];
  const [ticks, setTicks] = useState(() =>
    COMMODITY_SEEDS.filter((c) => pins.includes(c.code))
      .map((c) => ({ ...c, price: c.base, delta: 0 })),
  );
  useEffect(() => {
    setTicks(COMMODITY_SEEDS.filter((c) => pins.includes(c.code))
      .map((c) => ({ ...c, price: c.base, delta: 0 })));
  }, [pins.join(",")]);  // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!pins.length) return;
    const id = setInterval(() => {
      setTicks((prev) => prev.map((c) => {
        const drift = (Math.random() - 0.5) * 0.007 * c.base;
        const next = Math.max(c.base * 0.97, Math.min(c.base * 1.03, c.price + drift));
        return { ...c, price: next, delta: next - c.base };
      }));
    }, 3000);
    return () => clearInterval(id);
  }, [pins.length]);
  if (!pins.length) return null;
  return (
    <div className="flex flex-wrap gap-2 mb-4" data-testid="space-age-pinned-strip">
      {ticks.map((c) => {
        const up = c.delta >= 0;
        return (
          <div
            key={c.code}
            data-testid={`space-age-pinned-tile-${c.code.toLowerCase()}`}
            className="rounded-xl px-3 py-2 border border-cyan-400/60 flex flex-col"
            style={{
              background: "linear-gradient(180deg, rgba(6,25,55,0.9), rgba(2,6,23,0.95))",
              boxShadow: "0 0 12px rgba(6,182,212,0.45)",
              minWidth: "140px",
            }}
          >
            <div className="flex items-center gap-1.5 text-[9px] font-mono uppercase tracking-widest text-cyan-300">
              📌 {c.code} · {c.label}
            </div>
            <div className="text-lg font-bold text-white tabular-nums" style={{ textShadow: "0 0 8px rgba(6,182,212,0.65)" }}>
              ${formatPrice(c.price)}
            </div>
            <div className="text-[9px] font-mono tabular-nums" style={{ color: up ? "#6ee7b7" : "#fda4af" }}>
              {up ? "▲" : "▼"} {Math.abs(c.delta).toFixed(2)} · {c.unit}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Field Dispatch Weather HUD — deterministic diurnal curve + manager-set
// min/max thresholds fire a pulsing crimson toast when the temp falls out
// of the safe range.
// ---------------------------------------------------------------------------
// UNIT LOCK · American Fahrenheit (°F) is the hardcoded native standard for
// this HUD — every temperature value in this component is in °F, every
// threshold comparison is in °F, every toast label prints °F. No metric /
// Celsius fallback path. Do not change without a product decision.
// ---------------------------------------------------------------------------
const WEATHER_DEFAULT_MIN_F = 32;   // freezing point — safe cold floor (isolated)
const WEATHER_DEFAULT_MAX_F = 95;   // NWS Heat Advisory ceiling (isolated)
const WEATHER_GEO_CACHE_KEY = "rs.weather.geo.v2";
const WEATHER_GEO_TTL_MS = 24 * 60 * 60 * 1000; // 24h cache
const WEATHER_COORDS_CACHE_KEY = "rs.weather.coords.v1";
const WEATHER_COORDS_TTL_MS = 24 * 60 * 60 * 1000;

// ── Meteor Shower Intensity ───────────────────────────────────────────────────
// Controls CosmicWeatherCanvas New Moon burst cadence. Three presets mirror the
// real-sky spectrum: Rare (thin shower), Normal (typical Leonids-class), Storm
// (peak Perseids/Leonids outburst). Persisted to localStorage so the preference
// survives page reloads. Broadcast via "rs:meteor:intensity-change" CustomEvent
// so the canvas paint loop reads meteorIntensityCfgRef.current on the next RAF
// tick — zero re-render latency, zero structural drift.
const METEOR_INTENSITY_LS_KEY = "rs.meteor.intensity.v1";
const METEOR_INTENSITIES = {
  rare:   { delayMin: 25000, delayMax: 40000, countMin: 2, countMax: 3 },
  normal: { delayMin:  9000, delayMax: 18000, countMin: 3, countMax: 6 },
  storm:  { delayMin:  2000, delayMax:  5000, countMin: 5, countMax: 8 },
};
const getStoredMeteorIntensityKey = () => {
  try {
    const k = localStorage.getItem(METEOR_INTENSITY_LS_KEY);
    return METEOR_INTENSITIES[k] !== undefined ? k : "normal";
  } catch { return "normal"; }
};
const getStoredMeteorIntensity = () => METEOR_INTENSITIES[getStoredMeteorIntensityKey()];

// Resolve the caller's { lat, lon } once per session and cache it. Prefers
// browser Geolocation; falls back to ipapi.co so live weather still works
// on desktops that block the geolocation permission prompt.
async function _resolveCoords() {
  try {
    const cached = JSON.parse(window.localStorage.getItem(WEATHER_COORDS_CACHE_KEY) || "null");
    if (cached && cached.at && Date.now() - cached.at < WEATHER_COORDS_TTL_MS && cached.lat != null) {
      return cached;
    }
  } catch { /* ignore */ }
  const persist = (lat, lon, source) => {
    try {
      window.localStorage.setItem(WEATHER_COORDS_CACHE_KEY, JSON.stringify({ lat, lon, source, at: Date.now() }));
    } catch { /* ignore */ }
    return { lat, lon, source };
  };
  if ("geolocation" in navigator) {
    try {
      const pos = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 6000, maximumAge: 3600000 });
      });
      return persist(pos.coords.latitude, pos.coords.longitude, "geolocation");
    } catch { /* fall through */ }
  }
  try {
    const resp = await fetch("https://ipapi.co/json/");
    if (resp.ok) {
      const j = await resp.json();
      if (j && typeof j.latitude === "number") return persist(j.latitude, j.longitude, "ipapi");
    }
  } catch { /* silent */ }
  return null;
}

// Reverse-geocode the caller's coordinates to City, State. Uses OpenWeather
// Geocoding API when `REACT_APP_OPENWEATHER_API_KEY` is configured; otherwise
// gracefully falls back to `ipapi.co/json` (no key, HTTPS, 1k/day free tier)
// so the HUD ALWAYS resolves a real dispatch node without a paid key. Result
// is cached in localStorage for 24h so the network is only touched once/day.
async function _reverseGeocodeCityState() {
  try {
    const cached = JSON.parse(window.localStorage.getItem(WEATHER_GEO_CACHE_KEY) || "null");
    if (cached && cached.at && Date.now() - cached.at < WEATHER_GEO_TTL_MS && cached.city) {
      return cached;
    }
  } catch { /* ignore */ }
  const apiKey = process.env.REACT_APP_OPENWEATHER_API_KEY;
  const persist = (city, state, source) => {
    try {
      window.localStorage.setItem(WEATHER_GEO_CACHE_KEY, JSON.stringify({ city, state, source, at: Date.now() }));
    } catch { /* ignore */ }
    return { city, state, source };
  };
  // Prefer browser Geolocation → OpenWeather when key is present.
  if (apiKey && "geolocation" in navigator) {
    try {
      const pos = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 6000, maximumAge: 3600000 });
      });
      const { latitude, longitude } = pos.coords;
      const resp = await fetch(`https://api.openweathermap.org/geo/1.0/reverse?lat=${latitude}&lon=${longitude}&limit=1&appid=${apiKey}`);
      if (resp.ok) {
        const rows = await resp.json();
        const row = Array.isArray(rows) ? rows[0] : null;
        if (row && row.name) return persist(row.name, row.state || row.country || "", "openweather");
      }
    } catch { /* fall through to ipapi */ }
  }
  // ipapi.co free fallback — no key needed, HTTPS, returns city + region.
  try {
    const resp = await fetch("https://ipapi.co/json/");
    if (resp.ok) {
      const j = await resp.json();
      if (j && j.city) return persist(j.city, j.region || j.region_code || "", "ipapi");
    }
  } catch { /* silent */ }
  return null;
}

// Cosmic Radar Canvas — HTML5 <canvas> backdrop, requestAnimationFrame loop,
// pans stars horizontally at a speed proportional to `windMph`. Drifts a
// slow nebula radial gradient over top. Auto-scales to device pixel ratio
// and cleans up on unmount. Respects prefers-reduced-motion.
function CosmicWeatherCanvas({ windMph = 8, accent = "#06B6D4", isDaytime = true, isNewMoon = false }) {
  const canvasRef = useRef(null);
  const isDaytimeRef = useRef(isDaytime);
  useEffect(() => { isDaytimeRef.current = isDaytime; }, [isDaytime]);
  const isNewMoonRef = useRef(isNewMoon);
  useEffect(() => { isNewMoonRef.current = isNewMoon; }, [isNewMoon]);
  // Meteor intensity config ref — seeded from localStorage on mount so the
  // stored preset (Rare/Normal/Storm) takes effect on the very first RAF tick.
  const meteorIntensityCfgRef = useRef(getStoredMeteorIntensity());
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d");
    if (!ctx) return undefined;
    const reduced = typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let W = 0, H = 0, raf = 0;
    let stars = [];
    // Shooting stars — spawn every ~45s, sweep at -45° with a fading neon-cyan tail
    let shootingStars = [];
    let lastShootingSpawn = 0;
    const SHOOTING_INTERVAL_MS = 45_000;
    // New Moon Meteor Shower — burst pool, spawn timer, randomized interval
    let meteorBursts = [];
    let lastMeteorBurstAt = 0;
    let nextMeteorBurstDelay = 9000 + Math.random() * 9000; // 9–18 s until first burst
    // Constellation — triggered by external `constellation-trigger` event.
    // Bridges pairs of nearby stars into glowing linear vector strokes for
    // 3.5s before decaying back to the base starfield opacity.
    // (Elite tier confetti bursts render on the top-level EliteBurstCanvas
    // so they can erupt from the exact bounding-rect center of the Financial
    // Health Score card regardless of where the weather HUD is mounted.)
    let constellationUntil = 0;
    const onConstellation = () => { constellationUntil = performance.now() + 3500; };
    if (typeof window !== "undefined") window.addEventListener("constellation-trigger", onConstellation);

    const resize = () => {
      const r = canvas.getBoundingClientRect();
      W = Math.max(1, Math.floor(r.width));
      H = Math.max(1, Math.floor(r.height));
      canvas.width = W * dpr;
      canvas.height = H * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      // Seed ~1 star per 900 px² of canvas surface.
      const target = Math.min(120, Math.max(30, Math.round((W * H) / 900)));
      stars = new Array(target).fill(null).map(() => ({
        x: Math.random() * W,
        y: Math.random() * H,
        r: Math.random() * 1.2 + 0.3,
        tw: Math.random() * Math.PI * 2,   // twinkle phase
        ts: 0.5 + Math.random() * 1.5,     // twinkle speed
      }));
    };
    const paint = (t) => {
      const speed = 0.02 + Math.min(1.5, Number(windMph || 0) / 20); // px/frame
      // Wipe with a translucent inky-purple to leave a subtle motion trail.
      ctx.globalCompositeOperation = "source-over";
      ctx.fillStyle = "rgba(6, 8, 30, 0.32)";
      ctx.fillRect(0, 0, W, H);
      // Nebula radial cloud — pans on a very slow drift.
      const cx = W * (0.35 + 0.25 * Math.sin(t / 12000));
      const cy = H * (0.45 + 0.15 * Math.cos(t / 15000));
      const grad = ctx.createRadialGradient(cx, cy, 4, cx, cy, Math.max(W, H) * 0.65);
      grad.addColorStop(0.0, `${accent}55`);
      grad.addColorStop(0.35, `${accent}22`);
      grad.addColorStop(1.0, "rgba(6,8,30,0)");
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, W, H);
      // SPEC 3002: Atmospheric Aurora Ribbon — continuous Bezier-curve RAF
      // wave anchored near the card's top border, active in BOTH day and
      // night. Fixed 3-tone palette (neon-emerald / solar-gold /
      // twilight-blue) per spec. "Breathing" comes from a combined
      // sine+cosine amplitude modulation so the ribbon never moves on a
      // flat, mechanical loop. Opacity intensifies at night and stays
      // subtle by day so it never competes with the solar orb or the
      // daytime azure horizon strip. Drawn with "lighter" blending so it
      // glows on top of the nebula without muddying the deep-space base,
      // then stars/shooting-stars/constellation paint over it exactly as
      // before — zero changes to their code paths.
      ctx.globalCompositeOperation = "lighter";
      const ribbonOpacity = isDaytimeRef.current ? 0.09 : 0.17;
      const RIBBON_TONES = [
        { rgb: "52,211,153", rowIndex: 0 },  // neon-emerald
        { rgb: "251,191,36", rowIndex: 1 },  // solar-gold
        { rgb: "56,189,248", rowIndex: 2 },  // twilight-blue
      ];
      for (const tone of RIBBON_TONES) {
        const yBase = H * (0.10 + tone.rowIndex * 0.07);
        const breathe = Math.sin(t / 6000 + tone.rowIndex * 2.1) * 0.5
          + Math.cos(t / 8300 + tone.rowIndex * 1.3) * 0.5;
        const amp = H * (0.03 + 0.02 * Math.abs(breathe));
        const ribbonGrad = ctx.createLinearGradient(0, 0, W, 0);
        ribbonGrad.addColorStop(0.0, `rgba(${tone.rgb},0)`);
        ribbonGrad.addColorStop(0.5, `rgba(${tone.rgb},${ribbonOpacity})`);
        ribbonGrad.addColorStop(1.0, `rgba(${tone.rgb},0)`);
        const segments = 6;
        const segW = W / segments;
        ctx.beginPath();
        ctx.moveTo(0, yBase + Math.sin(t / 5000 + tone.rowIndex * 1.7) * amp);
        for (let i = 0; i < segments; i++) {
          const x0 = i * segW;
          const xMid = x0 + segW / 2;
          const xEnd = x0 + segW;
          const yMid = yBase + Math.sin(xMid / 90 + t / 5000 + tone.rowIndex * 1.7) * amp;
          const yEnd = yBase + Math.cos(xEnd / 110 + t / 6500 + tone.rowIndex * 1.7) * amp;
          ctx.quadraticCurveTo(xMid, yMid, xEnd, yEnd);
        }
        ctx.lineTo(W, yBase + H * 0.22);
        ctx.lineTo(0, yBase + H * 0.22);
        ctx.closePath();
        ctx.fillStyle = ribbonGrad;
        ctx.fill();
      }
      // Starlight matrix — pan + twinkle, opacity pulses with the frame time.
      ctx.globalCompositeOperation = "lighter";
      for (const s of stars) {
        s.x -= speed;
        if (s.x < -2) { s.x = W + 2; s.y = Math.random() * H; }
        s.tw += 0.03 * s.ts;
        const alpha = 0.55 + 0.45 * Math.sin(s.tw);
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,255,255,${alpha.toFixed(3)})`;
        ctx.fill();
      }
      // ---- Shooting stars — spawn cadence + physics update ----
      if (t - lastShootingSpawn > SHOOTING_INTERVAL_MS) {
        lastShootingSpawn = t;
        shootingStars.push({
          x: Math.random() * W * 0.6, // upper-left origin band
          y: -10,
          vx: 9.5,   // px/frame — high-velocity diagonal
          vy: 9.5,   // symmetric → -45° trajectory (down-right)
          life: 0,
          maxLife: 60,
        });
      }
      for (let i = shootingStars.length - 1; i >= 0; i--) {
        const sh = shootingStars[i];
        sh.x += sh.vx;
        sh.y += sh.vy;
        sh.life += 1;
        const fade = 1 - sh.life / sh.maxLife;
        if (fade <= 0 || sh.x > W + 30 || sh.y > H + 30) {
          shootingStars.splice(i, 1);
          continue;
        }
        // Tail — draw a fading neon-cyan line trailing behind the head
        const tailLen = 90;
        const tx = sh.x - (sh.vx / Math.hypot(sh.vx, sh.vy)) * tailLen;
        const ty = sh.y - (sh.vy / Math.hypot(sh.vx, sh.vy)) * tailLen;
        const tailGrad = ctx.createLinearGradient(tx, ty, sh.x, sh.y);
        tailGrad.addColorStop(0.0, "rgba(34,211,238,0)");
        tailGrad.addColorStop(1.0, `rgba(34,211,238,${(0.9 * fade).toFixed(3)})`);
        ctx.strokeStyle = tailGrad;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(tx, ty);
        ctx.lineTo(sh.x, sh.y);
        ctx.stroke();
        // Head
        ctx.beginPath();
        ctx.arc(sh.x, sh.y, 2.2, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(224,255,255,${fade.toFixed(3)})`;
        ctx.fill();
      }
      // ---- Constellation — bridge neighboring stars with glowing strokes ----
      if (t < constellationUntil) {
        const remaining = (constellationUntil - t) / 3500; // 1 → 0
        const opacity = Math.min(1, remaining * 1.4) * 0.7;
        const MAX_LINK_DIST = 120;
        ctx.strokeStyle = `rgba(52, 211, 153, ${opacity.toFixed(3)})`;
        ctx.lineWidth = 0.8;
        ctx.shadowColor = "rgba(52,211,153,0.85)";
        ctx.shadowBlur = 6;
        for (let i = 0; i < stars.length; i++) {
          const a = stars[i];
          // Only pair each star with a couple of its closest neighbors to
          // avoid an O(n²) draw storm — sample every 3rd for the second peer.
          for (let j = i + 1; j < stars.length; j += 3) {
            const b = stars[j];
            const dx = a.x - b.x;
            const dy = a.y - b.y;
            const d2 = dx * dx + dy * dy;
            if (d2 < MAX_LINK_DIST * MAX_LINK_DIST) {
              ctx.beginPath();
              ctx.moveTo(a.x, a.y);
              ctx.lineTo(b.x, b.y);
              ctx.stroke();
            }
          }
        }
        ctx.shadowBlur = 0;
      }
      // ── New Moon Meteor Shower ────────────────────────────────────────────
      // When the phase is New Moon (isNewMoonRef) AND it is nighttime, spawn
      // recurring bursts of 3–6 extra-bright incandescent meteors fanned
      // across a ~50° arc. Each meteor gets a white-gold tail (130–200 px),
      // a 3–5 px white-hot head, and 1.2–1.7× the speed of ordinary shooting
      // stars. "lighter" (additive) blend — already set by the star loop —
      // makes them appear supernova-bright against the deep-space field.
      if (!isDaytimeRef.current && isNewMoonRef.current) {
        if (t - lastMeteorBurstAt > nextMeteorBurstDelay) {
          // Read the live intensity config from the ref — zero re-render.
          const iCfg = meteorIntensityCfgRef.current;
          lastMeteorBurstAt = t;
          nextMeteorBurstDelay = iCfg.delayMin + Math.random() * (iCfg.delayMax - iCfg.delayMin);
          const burstCount = iCfg.countMin + Math.floor(Math.random() * (iCfg.countMax - iCfg.countMin + 1));
          for (let b = 0; b < burstCount; b++) {
            // Fan ±25° around the canonical 45° diagonal so meteors radiate
            // from a shared upper-left origin point like a real shower radiant.
            const spreadRad = (Math.PI / 9) * ((b / Math.max(1, burstCount - 1)) - 0.5);
            const angle = Math.PI / 4 + spreadRad;
            const speed = 11.5 + Math.random() * 5.5;
            meteorBursts.push({
              x: Math.random() * W * 0.65,
              y: -14,
              vx: Math.cos(angle) * speed,
              vy: Math.sin(angle) * speed,
              life: 0,
              maxLife: 42 + Math.floor(Math.random() * 16),
              bright: 0.76 + Math.random() * 0.24,
            });
          }
        }
      }
      for (let i = meteorBursts.length - 1; i >= 0; i--) {
        const mb = meteorBursts[i];
        mb.x += mb.vx;
        mb.y += mb.vy;
        mb.life += 1;
        const fade = 1 - mb.life / mb.maxLife;
        if (fade <= 0 || mb.x > W + 50 || mb.y > H + 50) {
          meteorBursts.splice(i, 1);
          continue;
        }
        const tailLen = 130 + mb.bright * 70;
        const spd = Math.hypot(mb.vx, mb.vy);
        const tx = mb.x - (mb.vx / spd) * tailLen;
        const ty = mb.y - (mb.vy / spd) * tailLen;
        const mbGrad = ctx.createLinearGradient(tx, ty, mb.x, mb.y);
        mbGrad.addColorStop(0.0, "rgba(255,255,255,0)");
        mbGrad.addColorStop(0.55, `rgba(255,235,150,${(0.5 * mb.bright * fade).toFixed(3)})`);
        mbGrad.addColorStop(1.0, `rgba(255,255,245,${(mb.bright * fade).toFixed(3)})`);
        ctx.strokeStyle = mbGrad;
        ctx.lineWidth = 2.5;
        ctx.beginPath();
        ctx.moveTo(tx, ty);
        ctx.lineTo(mb.x, mb.y);
        ctx.stroke();
        // White-hot incandescent head
        ctx.beginPath();
        ctx.arc(mb.x, mb.y, 3.0 + mb.bright * 1.8, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,255,255,${(mb.bright * fade).toFixed(3)})`;
        ctx.shadowColor = "rgba(255,248,200,0.95)";
        ctx.shadowBlur = 12;
        ctx.fill();
        ctx.shadowBlur = 0;
      }
      if (!reduced) raf = requestAnimationFrame(paint);
    };
    resize();
    if (reduced) {
      // Draw one static frame + skip the loop for accessibility.
      paint(performance.now());
    } else {
      raf = requestAnimationFrame(paint);
    }
    window.addEventListener("resize", resize);
    // Live intensity swap — fired by MeteorIntensityDock when the user taps
    // Rare / Normal / Storm. Updates meteorIntensityCfgRef on the next RAF tick.
    const onMeteorIntensity = (e) => {
      const cfg = e.detail?.cfg;
      if (cfg && cfg.delayMin !== undefined) meteorIntensityCfgRef.current = cfg;
    };
    window.addEventListener("rs:meteor:intensity-change", onMeteorIntensity);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
      window.removeEventListener("rs:meteor:intensity-change", onMeteorIntensity);
      if (typeof window !== "undefined") window.removeEventListener("constellation-trigger", onConstellation);
    };
  }, [windMph, accent]);
  return (
    <canvas
      ref={canvasRef}
      data-testid="space-age-weather-canvas"
      aria-hidden="true"
      className="absolute inset-0 w-full h-full pointer-events-none will-change-transform"
      style={{
        zIndex: 0,
        borderRadius: "1rem",
        transform: "translateZ(0)",
        backfaceVisibility: "hidden",
        contain: "layout paint",
      }}
    />
  );
}

// -----------------------------------------------------------------
// EliteBurstCanvas — full-viewport, pointer-events-none canvas that
// renders 40-particle neon-cyan + electric-gold confetti bursts.
// Origin coordinates come dynamically from the DOM via the
// `elite-burst-at` window CustomEvent (viewport-space {x, y}). This
// keeps the burst anchored to the Financial Health Score card's
// live getBoundingClientRect() center regardless of scroll position
// or where the WeatherHUD is mounted. Silent — no audio. 1200ms decay.
// -----------------------------------------------------------------
export function EliteBurstCanvas() {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d");
    if (!ctx) return undefined;
    const reduced = typeof window !== "undefined"
      && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let W = 0, H = 0, raf = 0;
    let particles = [];
    const PALETTE = ["#22d3ee", "#67e8f9", "#facc15", "#fde047", "#fbbf24"];

    const resize = () => {
      W = window.innerWidth;
      H = window.innerHeight;
      canvas.width = W * dpr;
      canvas.height = H * dpr;
      canvas.style.width = W + "px";
      canvas.style.height = H + "px";
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const spawnBurst = (x, y) => {
      const now = performance.now();
      for (let i = 0; i < 40; i++) {
        const angle = (i / 40) * Math.PI * 2 + Math.random() * 0.4;
        const speed = 2.2 + Math.random() * 4.0;
        particles.push({
          x, y,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed,
          color: PALETTE[i % PALETTE.length],
          born: now,
          r: 1.8 + Math.random() * 1.6,
        });
      }
      if (!raf && !reduced) raf = requestAnimationFrame(paint);
    };

    const paint = (t) => {
      ctx.clearRect(0, 0, W, H);
      if (!particles.length) {
        raf = 0;
        return;
      }
      const LIFE = 1200;
      for (let i = particles.length - 1; i >= 0; i--) {
        const p = particles[i];
        const age = t - p.born;
        if (age >= LIFE) { particles.splice(i, 1); continue; }
        p.x += p.vx;
        p.y += p.vy;
        p.vx *= 0.985;
        p.vy = p.vy * 0.985 + 0.12;
        const fade = 1 - age / LIFE;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r * (0.5 + 0.5 * fade), 0, Math.PI * 2);
        ctx.fillStyle = p.color;
        ctx.globalAlpha = Math.max(0, fade);
        ctx.shadowColor = p.color;
        ctx.shadowBlur = 10;
        ctx.fill();
      }
      ctx.globalAlpha = 1;
      ctx.shadowBlur = 0;
      if (!reduced) raf = requestAnimationFrame(paint);
    };

    const onBurst = (e) => {
      const x = Number(e?.detail?.x);
      const y = Number(e?.detail?.y);
      if (Number.isFinite(x) && Number.isFinite(y)) spawnBurst(x, y);
    };

    resize();
    window.addEventListener("resize", resize);
    window.addEventListener("elite-burst-at", onBurst);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
      window.removeEventListener("elite-burst-at", onBurst);
    };
  }, []);
  return (
    <canvas
      ref={canvasRef}
      data-testid="elite-burst-canvas"
      aria-hidden="true"
      className="fixed inset-0 pointer-events-none"
      style={{ zIndex: 60 }}
    />
  );
}

// -----------------------------------------------------------------
// CockpitTelemetryScroller — slim, full-width marquee strip that rides
// the very top edge of every dashboard layout. Streams three rotating
// live-telemetry blocks in monospace + tabular-nums, hydrated from
// /api/stats/summary on mount so the ticker reflects the real tenant.
// Pure CSS animation (translateX loop), zero JS ticks, low CPU.
// -----------------------------------------------------------------
export function CockpitTelemetryScroller() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get("/stats/summary")
      .then((r) => { if (!cancelled) setStats(r.data || null); })
      .catch(() => { /* unauth or offline — fall through to illustrative */ });
    return () => { cancelled = true; };
  }, []);

  // Deductible margin — receipts flagged tax_deductible / total_spent
  const totalSpent = Number(stats?.total_spent || 0);
  const deductible = Number(stats?.deductible || 0);
  const marginPct = totalSpent > 0 ? ((deductible / totalSpent) * 100) : 0;
  const marginStr = totalSpent > 0
    ? `${marginPct >= 0 ? "+" : ""}${marginPct.toFixed(1)}%`
    : "+14.8%";

  // Next 1040-ES quarterly deadline — standard IRS calendar (Q1 4/15,
  // Q2 6/15, Q3 9/15, Q4 1/15 of next year). Returns the next date
  // strictly *after* today with a friendly countdown.
  const nextEstQuarter = (() => {
    const today = new Date();
    const y = today.getFullYear();
    const candidates = [
      new Date(y, 3, 15),      // Apr 15
      new Date(y, 5, 15),      // Jun 15
      new Date(y, 8, 15),      // Sep 15
      new Date(y + 1, 0, 15),  // Jan 15 next year
    ];
    const upcoming = candidates.find((d) => d > today) || candidates[3];
    const days = Math.max(0, Math.ceil((upcoming - today) / (1000 * 60 * 60 * 24)));
    const label = upcoming.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    return `${label} (${days}d)`;
  })();

  // Unlocked deductible dollars — cash-of-shield stat, currency formatted
  const deductibleStr = new Intl.NumberFormat("en-US", {
    style: "currency", currency: "USD", maximumFractionDigits: 2,
  }).format(deductible || 4120);

  // Slot 4 — live audit-risk composite (mirrors AuditRiskMeter formula
  // via the /stats/summary backend addition). Beacon copy adapts to tier.
  const riskScore = Number(stats?.audit_risk_score ?? 0);
  const riskTier = stats?.audit_risk_tier || "safe";
  const beacon = riskTier === "safe"
    ? "SECURITY BEACON · CLEAR"
    : riskTier === "moderate"
    ? "SECURITY BEACON · CAUTION"
    : "SECURITY BEACON · ELEVATED";
  const riskStr = stats
    ? `🛡️ LIVE AUDIT FREQUENCY RISK MARGIN: ${riskScore}% ${beacon}`
    : "🛡️ LIVE AUDIT FREQUENCY RISK MARGIN: 12% SECURITY BEACON · CLEAR";

  const messages = [
    `📈 OPERATIONAL DELTA: Deductible Margin Net ${marginStr} Year-to-Date Node`,
    `📅 IRS COMPLIANCE BEACON: Next Quarterly Estimated Tax Deadline · ${nextEstQuarter}`,
    `🧮 UNLOCKED DEDUCTIONS: Active Premium Tax Shield Accumulating +${deductibleStr}`,
    riskStr,
  ];
  // Duplicate the sequence so the translateX(-50%) loop reads as an infinite
  // seamless scroll without a hard reset seam mid-viewport.
  const doubled = [...messages, ...messages];
  return (
    <div
      data-testid="cockpit-telemetry-scroller"
      className="w-full bg-slate-900/20 backdrop-blur-sm py-1.5 border-b border-cyan-500/5 overflow-hidden relative"
      style={{ zIndex: 50 }}
      aria-hidden="true"
    >
      <div
        className="flex items-center gap-16 whitespace-nowrap font-mono tabular-nums text-xs text-cyan-400/80"
        style={{
          animation: "cockpit-telemetry-marquee 35s linear infinite",
          willChange: "transform",
          width: "max-content",
        }}
      >
        {doubled.map((m, i) => (
          <span key={i} className="tracking-wider">
            {m}
            <span className="mx-4 text-cyan-500/30">·</span>
          </span>
        ))}
      </div>
      <style>{`
        @keyframes cockpit-telemetry-marquee {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
      `}</style>
    </div>
  );
}

// -----------------------------------------------------------------
// CosmicStaticTouchLayer — mobile-first canvas overlay that spawns
// jagged branching electric-cyan bolts from every touchmove point.
// Uses passive listeners so scroll is never blocked; the canvas is
// pointer-events:none so it never intercepts taps. Bolts fade cleanly
// within ~200ms so the trail feels like crackling static rather than
// persistent paint. Zero CPU when the user isn't touching.
// -----------------------------------------------------------------
export function CosmicStaticTouchLayer() {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d");
    if (!ctx) return undefined;
    const reduced = typeof window !== "undefined"
      && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let W = 0, H = 0, raf = 0;
    let bolts = [];
    let dragging = false;
    // Global mouse-down state — set on `mousedown`, cleared on `mouseup`
    // / `mouseleave`. The desktop mousemove drag pipeline only spawns
    // sparks while this flag is true, mirroring the touch drag gesture.
    let isMouseDown = false;
    // Risk-adaptive palette — flips to rose/crimson when audit tier is high
    let riskTier = (typeof window !== "undefined" && window.__rsRiskTier) || "safe";
    const onRiskTier = (e) => {
      const next = e?.detail?.tier;
      if (next) riskTier = next;
    };
    // Elite combo throttle — the burst lifetime is 1200ms; don't re-spawn
    // faster than that while the finger is still inside the card.
    let lastEliteBurstAt = 0;
    if (typeof window !== "undefined") window.addEventListener("audit-risk-tier", onRiskTier);

    const resize = () => {
      // SPEC 2502: cache raw viewport integers from window — no getBoundingClientRect()
      // inside this function. For a fixed inset-0 canvas, window.innerWidth/Height
      // are exact and never trigger a layout flush (unlike getBoundingClientRect).
      W = window.innerWidth;
      H = window.innerHeight;
      canvas.width  = Math.round(W * dpr);
      canvas.height = Math.round(H * dpr);
      canvas.style.width  = W + "px";
      canvas.style.height = H + "px";
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    // SPEC 2502: static bolt pool capacity — pre-allocated ceiling of 150 concurrent
    // bolt objects. When the pool is saturated, spawnBoltsAt skips instantiation of
    // new branch sets to prevent unbounded array growth during high-frequency drag.
    // The 200ms bolt lifetime at 60fps means ≤12 frames per bolt — pool clears fast.
    const MAX_BOLTS = 150;

    const spawnBoltsAt = (x, y) => {
      const now = performance.now();
      if (bolts.length < MAX_BOLTS) {
        const n = 3 + Math.floor(Math.random() * 3); // 3..5 branches
        for (let i = 0; i < n; i++) {
          const baseAngle = Math.random() * Math.PI * 2;
          const length = 8 + Math.random() * 7;
          // Zig-zag: 3 segments
          const pts = [{ x, y }];
          let cx = x, cy = y, a = baseAngle;
          const segs = 3;
          for (let s = 0; s < segs; s++) {
            a += (Math.random() - 0.5) * 1.4;
            cx += Math.cos(a) * (length / segs);
            cy += Math.sin(a) * (length / segs);
            pts.push({ x: cx, y: cy });
          }
          bolts.push({ pts, born: now, life: 200 }); // 200ms fade
        }
      }

      // ---- Elite Combo Tripper ----
      // If the touch point is inside the Financial Health Score card AND
      // composite >= 80, dispatch a card-rect-anchored elite confetti burst.
      // Throttled to the 1200ms burst lifetime so continuous drag doesn't
      // stack fireworks over fireworks.
      try {
        const composite = Number(typeof window !== "undefined" ? window.__rsHealthComposite : 0);
        if (composite >= 80 && (now - lastEliteBurstAt) > 1200) {
          const card = document.querySelector('[data-testid="financial-health-score"]');
          if (card) {
            const r = card.getBoundingClientRect();
            // x/y are clientX/clientY (viewport-space) — r is also viewport-space.
            // Direct compare; no scroll subtraction needed.
            if (x >= r.left && x <= r.right && y >= r.top && y <= r.bottom) {
              lastEliteBurstAt = now;
              window.dispatchEvent(new CustomEvent("elite-burst-at", {
                detail: { x: r.left + r.width / 2, y: r.top + r.height / 2 },
              }));
            }
          }
        }
      } catch (_) { /* silent */ }
    };

    const paint = (t) => {
      // Base DPR transform only — coords in bolts are clientX/clientY
      // (viewport-space), drawn 1:1 on the fixed viewport canvas.
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, W, H);
      if (!bolts.length) {
        raf = 0;
        return;
      }
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      for (let i = bolts.length - 1; i >= 0; i--) {
        const b = bolts[i];
        const age = t - b.born;
        if (age >= b.life) { bolts.splice(i, 1); continue; }
        const fade = 1 - age / b.life;
        // Palette adapts to audit-risk tier — cyan/blue in Safe or Moderate
        // (crisp neon default), rose/crimson in Critical to broadcast fiscal
        // exposure via the touch trail itself.
        const p0 = b.pts[0];
        const pN = b.pts[b.pts.length - 1];
        const grad = ctx.createLinearGradient(p0.x, p0.y, pN.x, pN.y);
        if (riskTier === "high") {
          grad.addColorStop(0, `rgba(255, 228, 230, ${(0.95 * fade).toFixed(3)})`);
          grad.addColorStop(0.5, `rgba(244, 63, 94, ${(0.9 * fade).toFixed(3)})`);
          grad.addColorStop(1, `rgba(190, 18, 60, ${(0.4 * fade).toFixed(3)})`);
          ctx.shadowColor = "#f43f5e";
        } else {
          grad.addColorStop(0, `rgba(224, 255, 255, ${(0.95 * fade).toFixed(3)})`);
          grad.addColorStop(0.5, `rgba(34, 211, 238, ${(0.85 * fade).toFixed(3)})`);
          grad.addColorStop(1, `rgba(59, 130, 246, ${(0.35 * fade).toFixed(3)})`);
          ctx.shadowColor = "#22d3ee";
        }
        ctx.strokeStyle = grad;
        ctx.lineWidth = 1.3;
        ctx.shadowBlur = 8;
        ctx.beginPath();
        ctx.moveTo(p0.x, p0.y);
        for (let k = 1; k < b.pts.length; k++) {
          ctx.lineTo(b.pts[k].x, b.pts[k].y);
        }
        ctx.stroke();
      }
      ctx.shadowBlur = 0;
      if (!reduced) raf = requestAnimationFrame(paint);
    };

     // ------------------------------------------------------------------
     // SPEC 2502: ZERO-REFLOW COORDINATE EXTRACTION
     // ------------------------------------------------------------------
     // For a `fixed inset-0` canvas, the element's origin is always viewport
     // (0, 0), so rect.left and rect.top are permanently zero — subtracting
     // them was a no-op that still triggered a full layout flush on every
     // pointer event.  Using clientX/clientY directly eliminates the
     // getBoundingClientRect() call and all associated layout recalculation.
     const pointerXY = (e) => {
       const t = e?.touches?.[0];
       const x = (e.clientX ?? t?.clientX) || 0;
       const y = (e.clientY ?? t?.clientY) || 0;
       return { x, y };
     };

     // ---- Touch pipeline (viewport-space clientX/clientY) ----
     const onTouchStart = (e) => {
       isMouseDown = true;
       dragging = true;
       const { x, y } = pointerXY(e);
       spawnBoltsAt(x, y);
       if (!raf && !reduced) raf = requestAnimationFrame(paint);
     };
     const onTouchMove = (e) => {
       if (!dragging) return;
       const { x, y } = pointerXY(e);
       spawnBoltsAt(x, y);
       if (!raf && !reduced) raf = requestAnimationFrame(paint);
     };
     const onTouchEnd = () => { isMouseDown = false; dragging = false; };

     // ---- Desktop click-and-drag pipeline (viewport-space clientX/clientY) ----
     const onMouseDown = (e) => {
       isMouseDown = true;
       dragging = true;
       const { x, y } = pointerXY(e);
       spawnBoltsAt(x, y);
       if (!raf && !reduced) raf = requestAnimationFrame(paint);
     };
     const onMouseMove = (e) => {
       if (!isMouseDown) return;
       const { x, y } = pointerXY(e);
       spawnBoltsAt(x, y);
       if (!raf && !reduced) raf = requestAnimationFrame(paint);
     };
     const onMouseUp = () => { isMouseDown = false; dragging = false; };
     const onMouseLeaveDoc = () => { isMouseDown = false; dragging = false; };

    resize();
    window.addEventListener("resize", resize);
    // Coordinates are now clientX/clientY (viewport-space = canvas-space for
    // a fixed inset-0 canvas). No CSS scroll transform needed — the canvas
    // stays pinned at (0,0) of the viewport at all times. GPU compositing
    // is still promoted via willChange:"transform" on the JSX style.

    // Passive touch listeners — never block scroll, never call preventDefault.
    window.addEventListener("touchstart", onTouchStart, { passive: true });
    window.addEventListener("touchmove", onTouchMove, { passive: true });
    window.addEventListener("touchend", onTouchEnd, { passive: true });
    window.addEventListener("mousedown", onMouseDown);
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    document.documentElement.addEventListener("mouseleave", onMouseLeaveDoc);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
      window.removeEventListener("touchstart", onTouchStart);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchend", onTouchEnd);
      window.removeEventListener("mousedown", onMouseDown);
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
      document.documentElement.removeEventListener("mouseleave", onMouseLeaveDoc);
      if (typeof window !== "undefined") window.removeEventListener("audit-risk-tier", onRiskTier);
    };
  }, []);
  return (
    <canvas
      ref={canvasRef}
      data-testid="cosmic-static-touch-layer"
      aria-hidden="true"
      className="fixed inset-0 w-full h-full pointer-events-none will-change-transform"
      style={{
        zIndex: 99,
        mixBlendMode: "screen",
        transform: "translateZ(0)",
        backfaceVisibility: "hidden",
        contain: "strict",
      }}
    />
  );
}



// -----------------------------------------------------------------
// SPEC 1801: Master Celestial Engine Active Key ───────────────────
// Shared by SheetLightningOverlay, MicroStarsCanvas, and Profile.jsx.
// Persists across page refreshes via localStorage.  Default = true.
// Both canvas loops listen for "rs:engine:toggle" CustomEvent so the
// RAF kill/restart fires within the very next animation tick — zero
// framework re-renders, zero layout recalculation, zero frame drops.
const PRISM_ENGINE_ACTIVE_KEY = "rs.engine.active.v1";
const getStoredEngineActive = () => {
  try {
    const v = localStorage.getItem(PRISM_ENGINE_ACTIVE_KEY);
    return v === null ? true : v !== "false"; // default ON for all new profiles
  } catch { return true; }
};

// -----------------------------------------------------------------
// SheetLightningOverlay — timed background canvas that fires sharp,
// branching electric-cyan bolt trajectories deep within the nebula
// stellar clouds every 25-40 s. Uses an HTML5 <canvas> for true
// vector lightning rather than a CSS filter glow. Blend mode
// `plus-lighter` additively merges bolts with the nebula background
// for an unmistakable high-voltage glow burst. z-index -2 sits safely
// above the nebula PNG (-z-20) but below all glass cards (-z-1+).
// Total flash: 100ms ease-in peak → 300ms ease-out decay = 400ms.
// Respects prefers-reduced-motion. Silent. Pointer-events-none.
// -----------------------------------------------------------------
export function SheetLightningOverlay() {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d");
    if (!ctx) return undefined;
    const reduced = typeof window !== "undefined"
      && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let W = 0, H = 0;
    let cancelled = false;
    let rafId = null;
    // SPEC 1802: engine gate — initialised from localStorage, updated by CustomEvent.
    let engineActive = getStoredEngineActive();

    // Opacity animation state — driven entirely by requestAnimationFrame.
    const FADE_IN_MS  = 100;
    const FADE_OUT_MS = 300;
    let fadeDir   = 0;       // 0 = idle, 1 = fade-in, -1 = fade-out
    let fadeStart = 0;
    let isFiring  = false;
    // nextFireTime = 0 fires the first bolt on the very first rAF tick.
    let nextFireTime = 0;

    const resize = () => {
      // Bind canvas dimensions directly to exact window viewport metrics.
      W = window.innerWidth;
      H = window.innerHeight;
      canvas.width  = Math.round(W * dpr);
      canvas.height = Math.round(H * dpr);
      canvas.style.width  = W + "px";
      canvas.style.height = H + "px";
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    // Debounced resize — continuously updates canvas bounds dynamically on
    // screen scaling events without flooding the rAF loop with micro-redraws.
    let resizeTimer = null;
    const debouncedResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(resize, 120);
    };

    // Draw one jagged branching bolt from (x0,y0) downward into the nebula.
    const drawBolt = (x0, y0) => {
      let x = x0, y = y0;
      const pts = [[x, y]];
      const steps = 7 + Math.floor(Math.random() * 5);
      for (let i = 0; i < steps; i++) {
        x += (Math.random() - 0.5) * 140;
        y += 38 + Math.random() * 55;
        pts.push([x, y]);
        if (y > H * 0.82) break;
      }
      // Glow pass — wide stroke at low alpha for the halo.
      ctx.shadowColor = "#22d3ee";
      ctx.shadowBlur  = 16;
      ctx.strokeStyle = "rgba(224,255,255,0.50)";
      ctx.lineWidth   = 2.8;
      ctx.lineCap     = "round";
      ctx.lineJoin    = "round";
      ctx.beginPath();
      ctx.moveTo(pts[0][0], pts[0][1]);
      for (const [px, py] of pts.slice(1)) ctx.lineTo(px, py);
      ctx.stroke();
      // Core pass — bright cyan spine.
      ctx.shadowBlur  = 5;
      ctx.strokeStyle = "rgba(56,189,248,0.92)";
      ctx.lineWidth   = 1.3;
      ctx.beginPath();
      ctx.moveTo(pts[0][0], pts[0][1]);
      for (const [px, py] of pts.slice(1)) ctx.lineTo(px, py);
      ctx.stroke();
      // Branches — 2-3 sub-forks from random mid-points.
      const branchCount = 2 + Math.floor(Math.random() * 2);
      for (let b = 0; b < branchCount; b++) {
        const si = 1 + Math.floor(Math.random() * Math.max(1, pts.length - 2));
        let [bx, by] = pts[si];
        ctx.shadowBlur  = 9;
        ctx.strokeStyle = "rgba(34,211,238,0.72)";
        ctx.lineWidth   = 0.9;
        ctx.beginPath();
        ctx.moveTo(bx, by);
        const bSteps = 3 + Math.floor(Math.random() * 3);
        for (let s = 0; s < bSteps; s++) {
          bx += (Math.random() - 0.5) * 90;
          by += 28 + Math.random() * 38;
          ctx.lineTo(bx, by);
          if (by > H * 0.82) break;
        }
        ctx.stroke();
      }
    };

    // Continuous requestAnimationFrame loop — drives all opacity animation
    // and bolt scheduling. Fires the first bolt on the very first frame
    // (nextFireTime = 0 guarantees now >= nextFireTime immediately).
    // SPEC 1802: per-frame engine gate — if engine was turned off mid-flight,
    // cancel this RAF chain immediately and zero the handle.
    const rafLoop = (now) => {
      if (cancelled) return;
      if (!engineActive) { rafId = 0; return; }

      // ── Opacity animation (rAF-driven, not CSS transitions) ─────────
      if (fadeDir === 1) {
        // Fade in over FADE_IN_MS
        const p = Math.min(1, (now - fadeStart) / FADE_IN_MS);
        canvas.style.opacity = String(p);
        if (p >= 1) {
          // Hold 100ms then begin fade-out
          fadeDir = 0;
          window.setTimeout(() => {
            if (!cancelled) { fadeDir = -1; fadeStart = performance.now(); }
          }, 100);
        }
      } else if (fadeDir === -1) {
        // Fade out over FADE_OUT_MS
        const p = Math.min(1, (now - fadeStart) / FADE_OUT_MS);
        canvas.style.opacity = String(1 - p);
        if (p >= 1) {
          canvas.style.opacity = "0";
          ctx.clearRect(0, 0, W, H);
          fadeDir  = 0;
          isFiring = false;
          // Schedule next bolt 12-24s from now
          nextFireTime = now + 12000 + Math.random() * 12000;
        }
      }

      // ── Bolt ignition check ─────────────────────────────────────────
      if (!isFiring && !reduced && now >= nextFireTime) {
        isFiring = true;
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.clearRect(0, 0, W, H);
        const boltCount = 3 + Math.floor(Math.random() * 3);
        for (let i = 0; i < boltCount; i++) {
          drawBolt(W * (0.05 + Math.random() * 0.90), H * Math.random() * 0.28);
        }
        ctx.shadowBlur = 0;
        canvas.style.opacity = "0";
        fadeDir   = 1;
        fadeStart = now;
      }

      rafId = requestAnimationFrame(rafLoop);
    };

    resize();
    window.addEventListener("resize", debouncedResize);

    // ---- SPEC 1802: engine toggle listener for SheetLightningOverlay --------
    const onEngineToggle = (e) => {
      engineActive = e.detail?.active !== false;
      if (engineActive) {
        // Restart: prime a near-instant first bolt, then re-enter the RAF chain.
        if (!rafId && !cancelled) {
          nextFireTime = performance.now() + 1500;
          rafId = requestAnimationFrame(rafLoop);
        }
      } else {
        // Kill: cancel the RAF chain, clear canvas, force opacity 0.
        cancelAnimationFrame(rafId);
        rafId  = 0;
        isFiring = false;
        fadeDir  = 0;
        canvas.style.opacity = "0";
        ctx.clearRect(0, 0, W, H);
      }
    };
    window.addEventListener("rs:engine:toggle", onEngineToggle);

    // Auto-start: only mount the rAF loop if the engine is currently active.
    if (engineActive) {
      rafId = requestAnimationFrame(rafLoop);
    }
    return () => {
      cancelled = true;
      if (rafId) cancelAnimationFrame(rafId);
      clearTimeout(resizeTimer);
      window.removeEventListener("resize", debouncedResize);
      window.removeEventListener("rs:engine:toggle", onEngineToggle);
    };
  }, []);
  return (
    <canvas
      ref={canvasRef}
      data-testid="sheet-lightning-overlay"
      aria-hidden="true"
      className="fixed inset-0 pointer-events-none w-full h-full bg-transparent z-[10] mix-blend-screen will-change-transform"
      style={{
        opacity: 0,
        filter: "brightness(145%) contrast(120%)",
        transform: "translateZ(0)",
        backfaceVisibility: "hidden",
        contain: "strict",
      }}
    />
  );
}


// -----------------------------------------------------------------
// ── SPEC 1501/1502: Prism Speed Engine constants ─────────────────────────────
// Three multipliers applied to each star's per-star hueSpeed °/s value.
//   slow      (0.25) — gentle drift,  full cycle 29–120 s per star
//   normal    (1.00) — SPEC 1401 seed default, full cycle 7–30 s
//   overdrive (3.50) — hyper-frequency diamond flash, full cycle 2–8.5 s
const PRISM_SPEEDS = {
  slow:      0.25,
  normal:    1.00,
  overdrive: 3.50,
};
const PRISM_SPEED_LS_KEY = "rs.prism.speed.v1";
const getStoredPrismSpeed = () => {
  try {
    const k = localStorage.getItem(PRISM_SPEED_LS_KEY);
    return PRISM_SPEEDS[k] !== undefined ? PRISM_SPEEDS[k] : PRISM_SPEEDS.normal;
  } catch { return PRISM_SPEEDS.normal; }
};
const getStoredPrismSpeedKey = () => {
  try {
    const k = localStorage.getItem(PRISM_SPEED_LS_KEY);
    return PRISM_SPEEDS[k] !== undefined ? k : "normal";
  } catch { return "normal"; }
};

// ==========================================================================
// MicroStarsCanvas — SPEC 1401/1402: Diamond Prism Twinkle & Starburst Engine
// ==========================================================================
// SPEC 1401: Continuous hue rotation (hueBase + tSec × hueSpeed °/s) layered
//            with a per-star sine shimmer wave (±30°) cycles every star through
//            the full diamond-prism palette: ice-diamond white (hue ≈195°, sat
//            22%, lit 95%), hot electric magenta (hue 305°), neon cyan (hue
//            185°), deep space emerald (hue 152°), glowing liquid gold (hue 45°).
//            The ice-white zone is recovered by blending saturation down to 22%
//            and lightness up to 95% inside a ±45° window centered on hue 195°.
//            Every color transition is computed frame-by-frame via Math.sin().
// SPEC 1402: A shared cursorRef captures raw viewport coords from a passive
//            window mousemove listener.  Each frame every star computes its
//            Euclidean distance to the cursor.  Stars within BURST_RADIUS (48px)
//            snap burstIntensity to 1.0; others decay at 0.045/frame (~0.4 s).
//            A burst draws 6 sharp cross-hair raycasts (60° apart) from the star
//            edge outward 4–8px at near-white glow for maximum diamond sparkle.
// ==========================================================================

export function MicroStarsCanvas() {
  const canvasRef = useRef(null);
  const cursorRef = useRef({ x: -9999, y: -9999 });
  const speedRef  = useRef(getStoredPrismSpeed());   // SPEC 1502: prism speed multiplier
  const engineRef = useRef(getStoredEngineActive());  // SPEC 1802: master engine gate

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d");
    if (!ctx) return undefined;

    const dpr     = Math.min(window.devicePixelRatio || 1, 2);
    const reduced = typeof window !== "undefined"
      && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

    let W       = 0;
    let H       = 0;
    let stars   = [];
    let raf     = 0;
    let mounted = true;

    // ---- SPEC 1402: passive global cursor position tracker -----------------
    const onCursorMove = (e) => {
      cursorRef.current = { x: e.clientX, y: e.clientY };
    };
    window.addEventListener("mousemove", onCursorMove, { passive: true });

    // ---- SPEC 1502: prism speed event listener — instant multiplier swap ---
    // Fired by PrismSpeedDock when the user taps Slow / Normal / Overdrive.
    const onSpeedChange = (e) => {
      speedRef.current = e.detail?.multiplier ?? PRISM_SPEEDS.normal;
    };
    window.addEventListener("rs:prism:speed-change", onSpeedChange);

    // ---- SPEC 1802: master engine toggle listener --------------------------
    // Fired by Profile.jsx handleEngineToggle via "rs:engine:toggle" CustomEvent.
    //   OFF → cancelAnimationFrame: physically halts the RAF chain so the CPU
    //         loop stops entirely.  One final paint() call freezes the star field
    //         as a static mesh on the canvas (retains last rendered frame).
    //   ON  → restarts requestAnimationFrame(paint) if not already running.
    const onEngineToggle = (e) => {
      const nowActive = e.detail?.active !== false;
      engineRef.current = nowActive;
      if (nowActive) {
        // Restart the diamond-prism loop if it was halted.
        if (raf === 0 && mounted) {
          raf = requestAnimationFrame(paint);
        }
      } else {
        // Physically kill the RAF chain — CPU loop halts immediately.
        cancelAnimationFrame(raf);
        raf = 0;
        // Draw one final frozen static mesh frame (stars at current positions,
        // hues frozen at last computed values — engine = off guard skips RAF re-schedule).
        paint(performance.now());
      }
    };
    window.addEventListener("rs:engine:toggle", onEngineToggle);

    // ---- Canvas resize + star re-seeding -----------------------------------
    const resize = () => {
      W = window.innerWidth;
      H = window.innerHeight;
      canvas.width  = Math.round(W * dpr);
      canvas.height = Math.round(H * dpr);
      canvas.style.width  = W + "px";
      canvas.style.height = H + "px";
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const count = 40 + Math.floor(Math.random() * 21); // 40–60 stars
      stars = Array.from({ length: count }, () => ({
        x:              Math.random() * W,
        y:              Math.random() * H,
        r:              2.0 + Math.random() * 1.5,        // radius 2.0–3.5 px
        // Alpha oscillation (SPEC 1301 preserved)
        phase:          Math.random() * Math.PI * 2.0,
        freq:           0.3 + Math.random() * 1.2,
        // SPEC 1401 — diamond prism hue engine fields
        hueBase:        Math.random() * 360.0,            // starting hue angle 0°–360°
        hueSpeed:       12.0 + Math.random() * 38.0,      // rotation speed °/s (12–50)
        huePhase:       Math.random() * Math.PI * 2.0,    // secondary sine phase offset
        shimFreq:       0.8 + Math.random() * 2.2,        // shimmer frequency 0.8–3.0
        // SPEC 1402 — starburst intensity state (mutable animation variable)
        burstIntensity: 0.0,
      }));
    };

    let resizeTimer = null;
    const debouncedResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(resize, 120);
    };

    // ---- SPEC 1401: diamond prism HSL decomposition helper -----------------
    // Returns [hue, saturation, lightness] for a given raw hue angle.
    // Ice-diamond-white recovery zone centred on hue 195° (±45° blend window):
    //   — at hue 195°: saturation = 22%, lightness = 95%  (pure ice-white)
    //   — at ±45° from 195°: saturation = 100%, lightness = 55%  (vivid color)
    //   — smooth cosine-linear blend between the two extremes inside the window
    const prismHSL = (hue) => {
      const raw   = ((hue - 195.0) % 360.0 + 360.0) % 360.0;
      const wDist = raw > 180.0 ? 360.0 - raw : raw;         // shortest arc in degrees
      const wBlen = Math.max(0.0, 1.0 - wDist / 45.0);      // 1 at center, 0 beyond ±45°
      const sat   = 100.0 - 78.0 * wBlen;                    // 22% – 100% saturation
      const lit   = 55.0  + 40.0 * wBlen;                    // 55% – 95% lightness
      return [hue, sat, lit];
    };

    // ---- SPEC 1402 constants -----------------------------------------------
    const BURST_RADIUS = 48.0;  // cursor proximity threshold in logical px
    const BURST_DECAY  = 0.045; // burstIntensity subtracted per frame (~0.4 s full decay)
    const RAY_COUNT    = 6;     // 6 rays at 60° intervals (cross-hair + diagonals)

    // ---- Per-frame render loop ---------------------------------------------
    const paint = (t) => {
      if (!mounted) return;
      // SPEC 1802: Only re-schedule the next frame if the engine is active.
      // When engineRef.current = false (OFF), this call is the final freeze frame —
      // it draws the stars at their current coordinates without scheduling a successor,
      // leaving a static starfield mesh on the canvas with zero CPU overhead.
      if (engineRef.current) {
        raf = requestAnimationFrame(paint);
        if (reduced) return; // running + reduced motion → skip drawing, keep RAF alive
      }
      // Draw: executes on every active tick AND once for the OFF freeze-frame.
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.globalCompositeOperation = "source-over";
      ctx.clearRect(0, 0, W, H);

      const tSec = t * 0.001;               // convert DOMHighResTimeStamp to seconds
      const cx   = cursorRef.current.x;     // cursor logical viewport X
      const cy   = cursorRef.current.y;     // cursor logical viewport Y

      for (const s of stars) {
        // ---- SPEC 1401 + 1502: continuous hue rotation + sine shimmer ------
        // Base rotation: hue advances (hueSpeed × speedMultiplier °/s).
        // speedRef.current is 0.25 (slow), 1.0 (normal), or 3.5 (overdrive).
        const spd     = speedRef.current;
        const rotHue  = (s.hueBase + tSec * s.hueSpeed * spd) % 360.0;
        // Shimmer layer: ±30° sine oscillation also scaled by speed multiplier
        // so Overdrive produces rapid iridescent flash across all stars.
        const shimOff = 30.0 * Math.sin(s.shimFreq * spd * tSec + s.huePhase);
        const hue     = (rotHue + shimOff + 360.0) % 360.0;

        // Decompose hue angle into HSL values with ice-white zone applied
        const [H, S, L] = prismHSL(hue);

        // Alpha: sine pulse between floor 0.60 and ceiling 1.00 per star frequency
        const alpha = Math.max(0.60, Math.min(1.00,
          0.80 + 0.20 * Math.sin(s.freq * tSec + s.phase)
        ));

        // ---- SPEC 1402: Euclidean proximity → starburst trigger ------------
        const dx   = s.x - cx;
        const dy   = s.y - cy;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < BURST_RADIUS) {
          // Cursor is within proximity — snap burst intensity to 1.0 immediately
          s.burstIntensity = 1.0;
        } else if (s.burstIntensity > 0.0) {
          // Cursor moved away — decay burst intensity linearly toward 0
          s.burstIntensity = Math.max(0.0, s.burstIntensity - BURST_DECAY);
        }

        // Effective alpha: burst overrides to 1.0 at peak, follows sine otherwise
        const effectiveAlpha = Math.max(alpha, s.burstIntensity);

        // ---- Draw neon glow halo + filled star disc -------------------------
        // shadowColor always uses full saturation for a vivid corona bloom ring
        ctx.shadowColor = `hsl(${H.toFixed(1)}, 100%, 70%)`;
        ctx.shadowBlur  = s.r * (2.5 + 1.5 * s.burstIntensity); // halo expands at burst
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
        ctx.fillStyle = `hsla(${H.toFixed(1)}, ${S.toFixed(1)}%, ${L.toFixed(1)}%, ${effectiveAlpha.toFixed(3)})`;
        ctx.fill();

        // ---- SPEC 1402: 6-ray cross-hair starburst raycasts ----------------
        if (s.burstIntensity > 0.005) {
          // Ray length: 4 px at minimum burst → 8 px at full burst intensity
          const rayLen   = 4.0 + 4.0 * s.burstIntensity;
          // Ray origin: just outside the filled star disc boundary
          const rayStart = s.r + 0.5;
          // Ray line opacity proportional to burst intensity, capped at 0.95
          const rayAlpha = (s.burstIntensity * 0.95).toFixed(3);
          // Near-white glow color for maximum diamond-sparkle contrast
          const rayColor = `hsla(${H.toFixed(1)}, 80%, 92%, ${rayAlpha})`;

          ctx.shadowBlur  = 4.0;
          ctx.shadowColor = `hsl(${H.toFixed(1)}, 100%, 80%)`;
          ctx.lineWidth   = 0.9;
          ctx.strokeStyle = rayColor;

          for (let ri = 0; ri < RAY_COUNT; ri++) {
            // Distribute all RAY_COUNT rays evenly around the full circle (60° apart)
            const angle = (ri / RAY_COUNT) * Math.PI * 2.0;
            const cosA  = Math.cos(angle);
            const sinA  = Math.sin(angle);
            // Ray start point: at the star disc outer edge
            const x1 = s.x + cosA * rayStart;
            const y1 = s.y + sinA * rayStart;
            // Ray end point: rayLen logical pixels further along the same angle
            const x2 = s.x + cosA * (rayStart + rayLen);
            const y2 = s.y + sinA * (rayStart + rayLen);
            ctx.beginPath();
            ctx.moveTo(x1, y1);
            ctx.lineTo(x2, y2);
            ctx.stroke();
          }
        }
      }

      // Reset shadow state — prevents glow bleed into the next frame clear
      ctx.shadowBlur = 0;
    };

    resize();
    window.addEventListener("resize", debouncedResize);
    // SPEC 1802: only start the RAF loop if the engine is currently active.
    // If the user had previously turned it off, the canvas stays blank on mount.
    if (engineRef.current) {
      raf = requestAnimationFrame(paint);
    }

    return () => {
      mounted = false;
      cancelAnimationFrame(raf);
      clearTimeout(resizeTimer);
      window.removeEventListener("resize", debouncedResize);
      window.removeEventListener("mousemove", onCursorMove);
      window.removeEventListener("rs:prism:speed-change", onSpeedChange);
      window.removeEventListener("rs:engine:toggle", onEngineToggle);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      data-testid="micro-stars-canvas"
      aria-hidden="true"
      className="fixed inset-0 pointer-events-none w-full h-full bg-transparent z-[10] will-change-transform"
      style={{
        transform: "translateZ(0)",
        backfaceVisibility: "hidden",
        contain: "strict",
      }}
    />
  );
}


export function WeatherHUD() {
  const { user } = useAuth();
  const [snapshot, setSnapshot] = useState(null);
  const [geoNode, setGeoNode] = useState(null);   // reverse-geocoded {city, state, source}
  const lastAlertRef = useRef(0);
  // Moonrise Toast — tracks the previous tick's isDaytime so we can detect
  // the exact day→night transition (moonrise) edge, firing at most once per
  // transition rather than once per poll interval.
  const prevIsDaytimeRef = useRef(null);
  // Resolve display thresholds — minF renders in the threshold chip;
  // maxF gates the heat advisory toast. Both fall back to ENV defaults
  // so a stale or unset user preference degrades gracefully.
  const rawMin = user?.space_age_weather_min;
  const rawMax = user?.space_age_weather_max;
  const minF = rawMin != null && Number(rawMin) > 0 ? Number(rawMin) : WEATHER_DEFAULT_MIN_F;
  const maxF = rawMax != null && Number(rawMax) > 0 ? Number(rawMax) : WEATHER_DEFAULT_MAX_F;
  // Kick off reverse geocoding once per session. Prefers persisted user
  // profile → cached localStorage → OpenWeather (with key) → ipapi.co free.
  useEffect(() => {
    if (user?.city || user?.location_city) return;  // profile wins
    let cancelled = false;
    _reverseGeocodeCityState().then((r) => { if (!cancelled && r) setGeoNode(r); });
    return () => { cancelled = true; };
  }, [user?.city, user?.location_city]);
  // Localized city/state badge — profile → reverse-geocode → 'Local Faction'.
  const locationLabel = (() => {
    const city = user?.city || user?.location_city || geoNode?.city;
    const state = user?.state || user?.location_state || user?.region || geoNode?.state;
    if (city && state) return `${city}, ${state} Node`;
    if (city) return `${city} Node`;
    if (state) return `${state} Node`;
    return "Local Faction";
  })();
  useEffect(() => {
    // Deterministic diurnal fallback — only used when the OpenWeather key is
    // NOT configured, so the HUD never blanks on unkeyed previews.
    // SPEC 1703: isDaytime derived from UNIX timestamp comparison against
    // computed solar window — zero hardcoded clock-hour constants.
    const computeSimulated = () => {
      const h = new Date().getHours();
      const dtNow   = Math.floor(Date.now() / 1000);       // current UTC Unix seconds
      // Browser timezone offset in seconds (positive = east of UTC).
      const localSec = ((dtNow - new Date().getTimezoneOffset() * 60) % 86400 + 86400) % 86400;
      // Compute today's simulated sunrise/sunset as absolute UTC Unix timestamps:
      //   simSunrise = 06:00 local → UTC     simSunset = 19:30 local → UTC
      const simSunrise = dtNow - localSec + 21600;
      const simSunset  = dtNow - localSec + 70200;
      const isDaytime  = dtNow >= simSunrise && dtNow < simSunset;
      // baseF/tempF are ALWAYS in °F. See UNIT LOCK comment above.
      const baseF = 58 + Math.sin(((h - 7) / 24) * Math.PI * 2) * 12;
      const tempF = Math.round(baseF + (Math.random() - 0.5) * 3);
      const windMph = Math.round(4 + Math.random() * 9);
      const humidity = Math.round(52 + (Math.random() - 0.5) * 20);
      const conditions = humidity > 68
        ? ["Marine Layer", "Overcast", "Light Drizzle"]
        : isDaytime
          ? ["Full Sun", "Partly Cloudy", "Dry Winds"]
          : ["Clear · Night Ops", "Cool Breeze"];
      const label = conditions[h % conditions.length];
      // SPEC 2102/2201: solar position percent for radial orb tracking (simulated).
      // Clamp to 5–95 (SPEC 2201) so the orb never clips out of the card boundary.
      const simElapsed      = dtNow - simSunrise;
      const simDaylight     = simSunset - simSunrise;
      const solarPositionPercent = Math.max(5, Math.min(95,
        (simElapsed / simDaylight) * 100
      ));
      // SPEC 3002: lunar position percent — same clamp pattern as the solar
      // orb, tracking the night window (today's sunset → tomorrow's
      // sunrise) so the moon glides the opposite half of the day/night arc.
      const simNightStart = simSunset;
      const simNightEnd = simSunrise + 86400;
      let simNightElapsed = dtNow - simNightStart;
      if (simNightElapsed < 0) simNightElapsed += 86400;
      const simNightDuration = simNightEnd - simNightStart;
      const moonPositionPercent = Math.max(5, Math.min(95,
        (simNightElapsed / simNightDuration) * 100
      ));
      return { tempF, windMph, humidity, label, stamp: new Date(), source: "sim", isDaytime, solarPositionPercent, moonPositionPercent };
    };
    // Live OpenWeather Current Weather endpoint — hit when the API key is
    // configured via REACT_APP_OPENWEATHER_API_KEY. Uses `units=imperial`
    // so the payload is already in °F + mph — no unit conversion drift.
    const apiKey = process.env.REACT_APP_OPENWEATHER_API_KEY;
    const fetchLive = async () => {
      if (!apiKey) return null;
      const coords = await _resolveCoords();
      if (!coords) return null;
      try {
        const url = `https://api.openweathermap.org/data/2.5/weather?lat=${coords.lat}&lon=${coords.lon}&units=imperial&appid=${apiKey}`;
        const resp = await fetch(url);
        if (!resp.ok) return null;
        const j = await resp.json();
        const tempF = Math.round(Number(j?.main?.temp));
        const windMph = Math.round(Number(j?.wind?.speed || 0));
        const humidity = Math.round(Number(j?.main?.humidity || 0));
        const label = String(j?.weather?.[0]?.main || j?.weather?.[0]?.description || "Clear");
        // SPEC 1901: Satellite timezone offset translation — add j.timezone (the
        // signed UTC offset in seconds, auto-updated by OpenWeather for DST) to
        // all three reference points before comparing.  Translating all values by
        // the same offset collapses the UTC epoch math into a pure local-time window
        // check: localCurrentTime falls between localSunriseTime and localSunsetTime
        // iff the observer at the queried coordinates is currently in daylight.
        // j.timezone is authoritative for the city being queried and already accounts
        // for every regional Daylight Saving Time transition automatically.
        const sunriseUnix = Number(j?.sys?.sunrise || 0);
        const sunsetUnix  = Number(j?.sys?.sunset  || 0);
        const tzOffset    = Number(j?.timezone || 0);
        const dtNow       = Math.round(Number(j?.dt || 0)) || Math.floor(Date.now() / 1000);

        // Apply the satellite timezone offset to produce local epoch timestamps.
        const localCurrentTime = dtNow       + tzOffset;
        const localSunriseTime = sunriseUnix + tzOffset;
        const localSunsetTime  = sunsetUnix  + tzOffset;

        // Primary path: timezone-translated window comparison — works correctly
        // across all global operator locations and pre-/post-DST curves with 0-second drift.
        // Fallback: when sunrise/sunset are absent, derive a simulated 06:00–19:30
        // local solar window from the same localCurrentTime reference.
        const isDaytime = (sunriseUnix > 0 && sunsetUnix > 0)
          ? (localCurrentTime >= localSunriseTime && localCurrentTime < localSunsetTime)
          : (() => {
              const localSec   = ((localCurrentTime % 86400) + 86400) % 86400;
              const simSunrise = localCurrentTime - localSec + 21600;  // 06:00 local
              const simSunset  = localCurrentTime - localSec + 70200;  // 19:30 local
              return localCurrentTime >= simSunrise && localCurrentTime < simSunset;
            })();
        // SPEC 2102/2201: solar position — fraction of the daylight window elapsed.
        // Clamp to 5–95 (SPEC 2201) so the orb never clips out of the card boundary.
        // elapsedSeconds and totalDaylightSeconds computed from the three
        // timezone-translated local timestamps per the spec binding.
        const elapsedSeconds       = localCurrentTime - localSunriseTime;
        const totalDaylightSeconds = localSunsetTime  - localSunriseTime;
        const solarPositionPercent = Math.max(5, Math.min(95,
          (elapsedSeconds / totalDaylightSeconds) * 100
        ));
        // SPEC 3002: lunar position — mirrors the solar orb math above but
        // tracks the night window (today's local sunset → tomorrow's local
        // sunrise), clamped 5–95 the same way.
        const nightWindowStart = localSunsetTime;
        const nightWindowEnd = localSunriseTime + 86400;
        let nightElapsedSeconds = localCurrentTime - nightWindowStart;
        if (nightElapsedSeconds < 0) nightElapsedSeconds += 86400;
        const totalNightSeconds = nightWindowEnd - nightWindowStart;
        const moonPositionPercent = Math.max(5, Math.min(95,
          (nightElapsedSeconds / totalNightSeconds) * 100
        ));
        if (!Number.isFinite(tempF)) return null;
        return { tempF, windMph, humidity, label, stamp: new Date(), source: "openweather", isDaytime, solarPositionPercent, moonPositionPercent, sunriseUnix, sunsetUnix, tzOffset };
      } catch {
        return null;
      }
    };
    const tick = async () => {
      const s = (await fetchLive()) || computeSimulated();
      setSnapshot(s);
      // Moonrise Toast — echoes the cold/heat advisory pattern below, but
      // fires on a completely different trigger: the exact day→night
      // transition edge (not a polling cadence), and only when tonight's
      // real synodic phase resolves to "Full Moon". prevIsDaytimeRef makes
      // this edge-triggered so it never re-fires on every 60s/300s poll —
      // only once, the instant isDaytime flips from true to false.
      if (prevIsDaytimeRef.current === true && s.isDaytime === false) {
        const moonriseFraction = getMoonPhaseFraction(s.stamp);
        if (getMoonPhaseName(moonriseFraction) === "Full Moon") {
          toast(
            <span className="inline-flex items-center gap-1.5">
              <Moon className="w-3.5 h-3.5" aria-hidden="true" />
              Full Moon rising — clear skies for the night watch
            </span>,
            {
              id: "wx-moonrise-full",
              duration: 6000,
              style: {
                background: "rgba(15,23,42,0.95)", color: "#e0f2fe",
                border: "1px solid rgba(148,163,184,0.55)",
                boxShadow: "0 0 18px rgba(148,163,184,0.55)",
                animation: "wx-moonrise-glow 1.6s ease-in-out infinite",
              },
            }
          );
        } else if (getMoonPhaseName(moonriseFraction) === "New Moon") {
          // New Moon Meteor Shower Toast — darkest skies, best meteor-watch
          // conditions. Fires once at the day→night transition, never on
          // subsequent polls. Paired indigo styling matches the canvas burst.
          toast(
            <span className="inline-flex items-center gap-1.5">
              <Moon className="w-3.5 h-3.5" aria-hidden="true" />
              New Moon · darkest skies — meteor shower conditions active
            </span>,
            {
              id: "wx-newmoon-meteor",
              duration: 7000,
              style: {
                background: "rgba(2,6,23,0.97)", color: "#e2e8f0",
                border: "1px solid rgba(99,102,241,0.50)",
                boxShadow: "0 0 18px rgba(99,102,241,0.40)",
              },
            }
          );
        }
      }
      prevIsDaytimeRef.current = s.isDaytime;
      // Heat advisory only — fires when tempF exceeds the configured MAX ceiling.
      // A reading at or below maxF fires nothing. Stable sonner id ensures any
      // stale variant is replaced in-place rather than stacked.
      const now = Date.now();
      const insideSafeBand = s.tempF <= maxF;
      if (insideSafeBand) return;
      if (now - lastAlertRef.current <= 90000) return;
      if (s.tempF > maxF) {
        lastAlertRef.current = now;
        toast(
          <span className="inline-flex items-center gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5" aria-hidden="true" />
            Heat advisory · {s.tempF}°F above {maxF}°F ceiling
          </span>,
          {
            id: "wx-heat-alert",
            duration: 6000,
            style: {
              background: "rgba(76,5,25,0.95)", color: "#fecaca",
              border: "1px solid rgba(244,63,94,0.75)",
              boxShadow: "0 0 18px rgba(244,63,94,0.75)",
              animation: "wx-alert-pulse 1.2s ease-in-out infinite",
            },
          }
        );
      }
    };
    tick();
    // Live API is polled every 5 minutes (OpenWeather free tier: 60/min,
    // 1M/mo). Simulated curve refreshes every 60s to stay lively.
    const intervalMs = apiKey ? 300000 : 60000;
    const id = setInterval(tick, intervalMs);
    try {
      toast.dismiss("wx-heat-alert");
    } catch { /* ignore */ }
    return () => clearInterval(id);
  }, [minF, maxF]);
  if (!snapshot) return null;
  const cold = snapshot.tempF < 55;
  const hot  = snapshot.tempF > 82;
  const accent = hot ? "#F59E0B" : cold ? "#60A5FA" : "#06B6D4";
  // Autonomous day/night state — sourced from sys.sunrise/sys.sunset when live,
  // simulated UNIX solar window when offline. snapshot always carries this field.
  const isDaytime = snapshot.isDaytime !== null && snapshot.isDaytime !== undefined
    ? snapshot.isDaytime
    : (() => {
        // Render-time safety net — both fetch paths always set isDaytime, so this
        // branch executes only on the very first tick before any snapshot arrives.
        const dtNow    = Math.floor(Date.now() / 1000);
        const localSec = ((dtNow - new Date().getTimezoneOffset() * 60) % 86400 + 86400) % 86400;
        return dtNow >= (dtNow - localSec + 21600) && dtNow < (dtNow - localSec + 70200);
      })();
  // Day: deep-space azure horizon mesh. Night: deep navy ambient depth.
  // 800ms ease transition keeps the visual shift smooth as day turns to night.
  const cardBg = isDaytime
    ? "linear-gradient(180deg, rgba(10,50,100,0.88) 0%, rgba(14,38,72,0.92) 50%, rgba(12,22,48,0.90) 100%)"
    : "linear-gradient(180deg, rgba(6,25,55,0.85), rgba(15,23,42,0.9))";
  const cardShadow = isDaytime
    ? `0 -1px 0 rgba(56,189,248,0.28), 0 0 22px ${accent}33`
    : `0 0 22px ${accent}33`;
  // "Operations Safe" chip flips to Cold/Heat advisory when temp falls
  // outside the resolved °F threshold band.
  const breachesMin = snapshot.tempF < minF;
  const breachesMax = snapshot.tempF > maxF;
  const opsStatus = breachesMin ? "Cold Advisory" : breachesMax ? "Heat Advisory" : "Operations Safe";
  const opsColor  = breachesMin || breachesMax ? "#F43F5E" : "#6ee7b7";
  // Weather Card Pulse — subtle "alive" heartbeat that kicks in whenever the
  // current label reads as a wet/volatile condition (rain, storm, drizzle,
  // thunder, shower, snow). Purely cosmetic — layered on top of the existing
  // box-shadow via a dedicated keyframe, never replaces it when idle.
  const STORMY_KEYWORDS = ["rain", "storm", "drizzle", "thunder", "shower", "snow"];
  const isStormy = STORMY_KEYWORDS.some((k) => snapshot.label.toLowerCase().includes(k));
  // SPEC 2102: solar orb position — fraction of daylight window elapsed (0–100).
  // Sourced from snapshot (both fetchLive and computeSimulated set this field).
  const solarPositionPercent = snapshot.solarPositionPercent ?? 50;
  // SPEC 3002: lunar orb position — mirrors the solar orb, fraction of the
  // night window elapsed (0–100). Sourced from snapshot the same way.
  const moonPositionPercent = snapshot.moonPositionPercent ?? 50;
  // Moon Phase Cycle — real-world synodic phase for tonight, drives the
  // lunar orb's glow intensity so it visibly waxes/wanes night-to-night
  // instead of glowing at a fixed brightness every night.
  const moonPhaseFraction = getMoonPhaseFraction(snapshot.stamp);
  const moonIllumination = getMoonIllumination(moonPhaseFraction);
  const moonPhaseName = getMoonPhaseName(moonPhaseFraction);
  // New Moon flag — true only at night so the canvas burst fires exclusively
  // in darkness (the real-world condition for dark-sky meteor observation).
  const isNewMoon = moonPhaseName === "New Moon" && !isDaytime;
  // Moon Crescent Shape — true silhouette masking instead of a flat opacity
  // dim. A dark "shadow disc" of the exact same diameter as the lit disc is
  // translated across it by a percentage of that diameter: 0% = fully
  // overlapping (new moon, disc reads dark), 100%/-100% = fully clear of
  // the lit disc (full moon, no shadow visible at all). Sign flips at the
  // phase-0.5 midpoint so the shadow slides in from the left while waxing
  // and from the right while waning — mirroring the real terminator path.
  const moonShadowOffsetPct = (moonPhaseFraction < 0.5 ? -1 : 1) * moonIllumination * 100;
  return (
    <div
      className="rounded-2xl p-4 border relative overflow-hidden"
      style={{
        borderColor: isDaytime ? "rgba(56,189,248,0.50)" : accent + "60",
        background: cardBg,
        boxShadow: cardShadow,
        backdropFilter: "blur(16px)",
        WebkitBackdropFilter: "blur(16px)",
        transition: "background 800ms ease, box-shadow 800ms ease, border-color 800ms ease",
        animation: isStormy ? "wx-condition-pulse 2.6s ease-in-out infinite" : "none",
      }}
      data-testid="space-age-weather-hud"
      data-weather-pulse={isStormy ? "active" : "idle"}
    >
      {/* Daytime horizon luminance strip — azure sky-glow sweeps the top edge.
          CosmicWeatherCanvas particles scroll across the bar behind this strip. */}
      {isDaytime && (
        <div
          aria-hidden="true"
          className="absolute top-0 inset-x-0 pointer-events-none"
          style={{
            height: "2px",
            background: "linear-gradient(90deg, transparent 0%, rgba(56,189,248,0.55) 20%, rgba(224,255,255,0.80) 50%, rgba(56,189,248,0.55) 80%, transparent 100%)",
            borderRadius: "1rem 1rem 0 0",
          }}
        />
      )}
      {/* SPEC 3002: Daytime solar atmospheric wash — vibrant amber/orange
          tint layered above the canvas and beneath the orb/text so the
          card visibly "warms" while the sun is up. Purged at night in
          favor of the deep sapphire/black cardBg + stardust + lunar orb. */}
      {isDaytime && (
        <div
          aria-hidden="true"
          className="absolute top-0 inset-x-0 h-16 bg-gradient-to-r from-amber-500/20 via-orange-500/10 to-transparent pointer-events-none"
          style={{ zIndex: 4, borderRadius: "1rem 1rem 0 0" }}
        />
      )}
      {/* SPEC 2401/2402/2403/3002/3003: Hyper-polished prism-glow circular solar
          core orb — absolute positioned, tracks linearly across the card from
          sunrise (5%) to sunset (95%) per SPEC 2201. Compact 48px x 48px
          circular profile with a feathered radial-gradient mask and soft
          blur — zero flat/solid box fills, zero sharp edges. zIndex:15 floats
          the orb above CosmicWeatherCanvas (z-0) but beneath text headers
          (z-20) per SPEC 2301 stratification matrix. display:none at
          nightfall fully removes it from paint rather than sliding it
          off-canvas. */}
      <div
        aria-hidden="true"
        className="absolute top-1/2 -translate-y-1/2 rounded-full w-12 h-12 bg-gradient-radial from-amber-400 via-yellow-300/60 to-transparent blur-sm pointer-events-none transition-all duration-1000 ease-linear shadow-[0_0_20px_rgba(251,191,36,0.5)]"
        style={{ left: `${isDaytime ? solarPositionPercent : -20}%`, width: "48px", height: "48px", display: isDaytime ? 'block' : 'none', zIndex: 15 }}
      />
      {/* SPEC 3002/Moon Crescent Shape: Lunar orb indicator — nocturnal
          counterpart to the solar orb. Non-emoji, built from a lit silver-
          blue disc + a dark "shadow disc" clipped inside a circular overflow
          wrapper. The shadow disc is translated across the lit disc by
          moonShadowOffsetPct — a real crescent/gibbous silhouette instead of
          a flat opacity dim. Tracks the night window (today's sunset →
          tomorrow's sunrise), clamped 5%-95% using the exact same
          Math.max/Math.min pattern as the solar orb. Outer element carries
          the position/glow/z-index so the ambient halo is never clipped by
          the inner circle mask. */}
      <div
        aria-hidden="true"
        data-testid="space-age-lunar-orb"
        data-moon-phase={moonPhaseName}
        data-moon-illumination={moonIllumination.toFixed(3)}
        title={`${moonPhaseName} · ${Math.round(moonIllumination * 100)}% illuminated`}
        className="absolute top-1/2 -translate-y-1/2 rounded-full w-12 h-12 blur-[0.5px] pointer-events-none transition-all duration-1000 ease-linear"
        style={{
          left: `${!isDaytime ? moonPositionPercent : -20}%`,
          width: "48px",
          height: "48px",
          display: !isDaytime ? 'block' : 'none',
          zIndex: 15,
          boxShadow: `0 0 ${8 + 14 * moonIllumination}px rgba(148,163,184,${(0.25 + 0.35 * moonIllumination).toFixed(3)})`,
        }}
      >
        <div className="relative w-full h-full rounded-full overflow-hidden">
          {/* Lit disc — always painted at full base brightness. The
              silhouette (crescent/gibbous/full/new) is carved entirely by
              the shadow disc below, not by dimming this layer. */}
          <div
            className="absolute inset-0 rounded-full bg-gradient-radial from-slate-100/95 via-sky-200/60 to-sky-300/20"
            style={{ boxShadow: "inset -5px -4px 9px rgba(15,23,42,0.35), inset 4px 3px 7px rgba(15,23,42,0.22)" }}
          />
          {/* Shadow disc — same diameter as the lit disc, slid across it by
              moonShadowOffsetPct (a % of its own width, i.e. the diameter).
              0% = full overlap (new moon). ±100% = zero overlap (full moon). */}
          <div
            className="absolute inset-0 rounded-full"
            style={{
              transform: `translateX(${moonShadowOffsetPct}%)`,
              transition: "transform 1000ms ease-linear",
              background: "radial-gradient(circle, rgba(6,8,30,0.97) 55%, rgba(6,8,30,0.88) 100%)",
            }}
          />
        </div>
      </div>
      <CosmicWeatherCanvas windMph={snapshot.windMph} accent={accent} isDaytime={isDaytime} isNewMoon={isNewMoon} />
      <div className="relative z-20 flex items-center justify-between mb-2 flex-wrap gap-2">
        {/* SPEC 2101/3001: SVG sun icon and emoji text nodes removed — solar
            presence is now represented exclusively by the radial orb above,
            and the satellite glyph is a lucide-react <Satellite> icon. */}
        <div
          className="text-[9px] font-mono uppercase tracking-[0.28em] inline-flex items-center gap-1"
          style={{ color: accent }}
        >
          <Satellite className="w-3 h-3" aria-hidden="true" />
          Field Dispatch Weather HUD
          <span
            data-testid="space-age-daynight-indicator"
            className="ml-2 px-1.5 py-0.5 rounded text-[7px] tracking-widest"
            style={{
              background: isDaytime ? "rgba(56,189,248,0.15)" : "rgba(99,102,241,0.15)",
              border:     isDaytime ? "1px solid rgba(56,189,248,0.40)" : "1px solid rgba(99,102,241,0.40)",
              color:      isDaytime ? "#7dd3fc" : "#a5b4fc",
            }}
          >
            {isDaytime ? "DAY" : "NIGHT"}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span
            className="text-[9px] font-mono uppercase tracking-widest px-2 py-1 rounded-md inline-flex items-center gap-1"
            data-testid="space-age-dispatch-climate-chip"
            style={{
              background: `${opsColor}14`,
              border: `1px solid ${opsColor}80`,
              color: opsColor,
              textShadow: `0 0 8px ${opsColor}80`,
            }}
          >
            <Satellite className="w-3 h-3" aria-hidden="true" />
            Dispatch Climate: {opsStatus}
          </span>
          <span
            className="text-[8px] font-mono uppercase tracking-widest text-slate-500"
            data-testid="space-age-weather-source-chip"
            title={snapshot.source === "openweather" ? "Live OpenWeather Current data · units=imperial" : "Simulated diurnal curve · add REACT_APP_OPENWEATHER_API_KEY to switch to live"}
          >
            {snapshot.source === "openweather" ? "live · openweather" : "sim"} · {snapshot.stamp.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
          </span>
        </div>
      </div>
      <div className="relative z-20 flex items-end gap-4">
        <div
          className="text-4xl font-bold tabular-nums"
          data-testid="space-age-weather-temp"
          style={{ color: accent, textShadow: `0 0 12px ${accent}80`, letterSpacing: "-0.01em" }}
        >
          {/* Clean °F string · e.g. 47°F. tabular-nums keeps the digit column
              aligned regardless of temp; padStart on the integer guarantees
              a two-column readout with a stable padding rail. */}
          {String(Math.round(snapshot.tempF)).padStart(2, "\u2007")}
          <span className="align-baseline">°F</span>
        </div>
        <div className="pb-1 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="text-xs font-mono text-slate-200">{snapshot.label}</div>
            <span
              data-testid="space-age-weather-location-badge"
              className="inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-md"
              style={{
                background: `${accent}14`,
                border: `1px solid ${accent}55`,
                color: accent,
                textShadow: `0 0 6px ${accent}80`,
              }}
              title={geoNode?.source ? `Localized dispatch node · geocoded via ${geoNode.source}` : "Localized dispatch node"}
            >
              <MapPin className="w-3 h-3" aria-hidden="true" />
              {locationLabel}
            </span>
          </div>
          <div className="text-[10px] font-mono text-slate-400 flex gap-3 mt-1">
            <span className="inline-flex items-center gap-1">
              <Wind className="w-3 h-3" aria-hidden="true" />
              {snapshot.windMph} mph
            </span>
            <span className="inline-flex items-center gap-1">
              <Droplets className="w-3 h-3" aria-hidden="true" />
              {snapshot.humidity}%
            </span>
            <span data-testid="space-age-weather-threshold-chip" className="text-cyan-300 inline-flex items-center gap-1">
              <Target className="w-3 h-3" aria-hidden="true" />
              {minF}°F · {maxF}°F
            </span>
          </div>
        </div>
      </div>
      <style>{`
        @keyframes wx-alert-pulse {
          0%, 100% { box-shadow: 0 0 12px rgba(244,63,94,0.45); }
          50%      { box-shadow: 0 0 26px rgba(244,63,94,0.95); }
        }
        @keyframes wx-condition-pulse {
          0%, 100% { box-shadow: ${cardShadow}; }
          50%      { box-shadow: 0 -1px 0 rgba(56,189,248,0.35), 0 0 34px ${accent}66, 0 0 58px ${accent}30; }
        }
        @keyframes wx-moonrise-glow {
          0%, 100% { box-shadow: 0 0 10px rgba(148,163,184,0.45); }
          50%      { box-shadow: 0 0 22px rgba(226,232,240,0.85); }
        }
      `}</style>
    </div>
  );
}

// ── SPEC 1502: PrismSpeedDock — glass capsule docked in Control Deck header ───
// Three pill buttons (Slow / Normal / Overdrive) control the global hue
// rotation speed multiplier for MicroStarsCanvas.  Selection is persisted to
// localStorage (PRISM_SPEED_LS_KEY) and broadcast via "rs:prism:speed-change"
// so the canvas paint loop reads speedRef.current on the very next RAF tick —
// zero re-render latency, zero structural drift.
//
// Aura palette:
//   Slow      → space-emerald glow  (rgba(52,211,153,…))
//   Normal    → cobalt-cyan glow    (rgba(6,182,212,…))
//   Overdrive → hyper-amber glow    (rgba(245,158,11,…))
// ─────────────────────────────────────────────────────────────────────────────
const PRISM_PILL_CFG = {
  slow: {
    label: "Slow",
    activeBg:     "rgba(16,185,129,0.18)",
    activeBorder: "rgba(52,211,153,0.75)",
    activeColor:  "#6ee7b7",
    activeGlow:   "rgba(52,211,153,0.75)",
  },
  normal: {
    label: "Normal",
    activeBg:     "rgba(6,182,212,0.18)",
    activeBorder: "rgba(6,182,212,0.80)",
    activeColor:  "#67e8f9",
    activeGlow:   "rgba(6,182,212,0.75)",
  },
  overdrive: {
    label: "Overdrive",
    activeBg:     "rgba(245,158,11,0.18)",
    activeBorder: "rgba(245,158,11,0.80)",
    activeColor:  "#fcd34d",
    activeGlow:   "rgba(245,158,11,0.75)",
  },
};

export function PrismSpeedDock() {
  const [active, setActive] = useState(getStoredPrismSpeedKey);

  const handleSpeed = (key) => {
    try { localStorage.setItem(PRISM_SPEED_LS_KEY, key); } catch { /* ignore */ }
    setActive(key);
    window.dispatchEvent(
      new CustomEvent("rs:prism:speed-change", {
        detail: { speed: key, multiplier: PRISM_SPEEDS[key] },
      })
    );
  };

  return (
    <div
      data-testid="prism-speed-dock"
      style={{
        background: "rgba(6,8,30,0.82)",
        backdropFilter: "blur(18px)",
        WebkitBackdropFilter: "blur(18px)",
        border: "1px solid rgba(255,255,255,0.07)",
        borderRadius: "14px",
        padding: "10px 14px",
        display: "inline-flex",
        flexDirection: "column",
        gap: "7px",
      }}
    >
      <div
        style={{
          fontSize: "9px",
          fontFamily: "monospace",
          letterSpacing: "0.24em",
          textTransform: "uppercase",
          color: "rgba(148,163,184,0.65)",
          lineHeight: 1,
        }}
      >
        Prism Speed
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
        {Object.entries(PRISM_PILL_CFG).map(([key, cfg]) => {
          const isActive = active === key;
          return (
            <button
              key={key}
              type="button"
              onClick={() => handleSpeed(key)}
              data-testid={`prism-speed-${key}`}
              aria-pressed={isActive}
              style={{
                padding: "4px 12px",
                borderRadius: "999px",
                fontSize: "10px",
                fontFamily: "monospace",
                textTransform: "uppercase",
                letterSpacing: "0.14em",
                fontWeight: 600,
                background: isActive ? cfg.activeBg    : "rgba(255,255,255,0.03)",
                border:     `1px solid ${isActive ? cfg.activeBorder : "rgba(255,255,255,0.09)"}`,
                color:      isActive ? cfg.activeColor : "rgba(100,116,139,0.9)",
                boxShadow:  isActive
                  ? `0 0 12px ${cfg.activeGlow}, inset 0 0 6px ${cfg.activeBg}`
                  : undefined,
                transform:  isActive ? "scale(1.06)" : "scale(1)",
                transition: "all 0.18s ease",
                cursor: "pointer",
                lineHeight: "1.6",
                outline: "none",
              }}
            >
              {cfg.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── SPEC 1601: MeteorIntensityDock — glass capsule controlling New Moon burst cadence ──
// Three pill buttons (Rare / Normal / Storm) map to METEOR_INTENSITIES presets.
// Selection is persisted to localStorage (METEOR_INTENSITY_LS_KEY) and broadcast
// via "rs:meteor:intensity-change" so CosmicWeatherCanvas reads the config on
// the very next RAF tick — zero re-render latency, zero structural drift.
//
// Aura palette:
//   Rare   → deep indigo  (rgba(99,102,241,…))
//   Normal → cobalt-cyan  (rgba(6,182,212,…))
//   Storm  → hyper-violet (rgba(167,139,250,…))
// ─────────────────────────────────────────────────────────────────────────────
const METEOR_PILL_CFG = {
  rare: {
    label:        "Rare",
    activeBg:     "rgba(79,70,229,0.18)",
    activeBorder: "rgba(99,102,241,0.75)",
    activeColor:  "#a5b4fc",
    activeGlow:   "rgba(99,102,241,0.75)",
  },
  normal: {
    label:        "Normal",
    activeBg:     "rgba(6,182,212,0.18)",
    activeBorder: "rgba(6,182,212,0.80)",
    activeColor:  "#67e8f9",
    activeGlow:   "rgba(6,182,212,0.75)",
  },
  storm: {
    label:        "Storm",
    activeBg:     "rgba(139,92,246,0.18)",
    activeBorder: "rgba(167,139,250,0.80)",
    activeColor:  "#ddd6fe",
    activeGlow:   "rgba(167,139,250,0.85)",
  },
};

export function MeteorIntensityDock() {
  const [active, setActive] = useState(getStoredMeteorIntensityKey);

  const handleIntensity = (key) => {
    try { localStorage.setItem(METEOR_INTENSITY_LS_KEY, key); } catch { /* ignore */ }
    setActive(key);
    window.dispatchEvent(
      new CustomEvent("rs:meteor:intensity-change", {
        detail: { intensity: key, cfg: METEOR_INTENSITIES[key] },
      })
    );
  };

  return (
    <div
      data-testid="meteor-intensity-dock"
      style={{
        background:         "rgba(6,8,30,0.82)",
        backdropFilter:     "blur(18px)",
        WebkitBackdropFilter: "blur(18px)",
        border:             "1px solid rgba(255,255,255,0.07)",
        borderRadius:       "14px",
        padding:            "10px 14px",
        display:            "inline-flex",
        flexDirection:      "column",
        gap:                "7px",
      }}
    >
      <div
        style={{
          fontSize:      "9px",
          fontFamily:    "monospace",
          letterSpacing: "0.24em",
          textTransform: "uppercase",
          color:         "rgba(148,163,184,0.65)",
          lineHeight:    1,
        }}
      >
        Meteor Shower
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
        {Object.entries(METEOR_PILL_CFG).map(([key, cfg]) => {
          const isActive = active === key;
          return (
            <button
              key={key}
              type="button"
              onClick={() => handleIntensity(key)}
              data-testid={`meteor-intensity-${key}`}
              aria-pressed={isActive}
              style={{
                padding:       "4px 12px",
                borderRadius:  "999px",
                fontSize:      "10px",
                fontFamily:    "monospace",
                textTransform: "uppercase",
                letterSpacing: "0.14em",
                fontWeight:    600,
                background:    isActive ? cfg.activeBg    : "rgba(255,255,255,0.03)",
                border:        `1px solid ${isActive ? cfg.activeBorder : "rgba(255,255,255,0.09)"}`,
                color:         isActive ? cfg.activeColor : "rgba(100,116,139,0.9)",
                boxShadow:     isActive
                  ? `0 0 12px ${cfg.activeGlow}, inset 0 0 6px ${cfg.activeBg}`
                  : undefined,
                transform:     isActive ? "scale(1.06)" : "scale(1)",
                transition:    "all 0.18s ease",
                cursor:        "pointer",
                lineHeight:    "1.6",
                outline:       "none",
              }}
            >
              {cfg.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
const CURSOR_COLORS = {
  cyan:   { hex: "#06B6D4", r: 6,   g: 182, b: 212 },
  violet: { hex: "#8B5CF6", r: 139, g: 92,  b: 246 },
  amber:  { hex: "#F59E0B", r: 245, g: 158, b: 11  },
};
const COLOR_LS_KEY     = "rs.cursor.color.v1";
const INTENSITY_LS_KEY = "rs.cursor.intensity.v1";
const INTENSITY_COUNTS = { light: 3, medium: 7, intense: 14 };
const getStoredColor = () => {
  try { const k = localStorage.getItem(COLOR_LS_KEY); return CURSOR_COLORS[k] ? k : "cyan"; } catch { return "cyan"; }
};
const getStoredIntensity = () => {
  try { const k = localStorage.getItem(INTENSITY_LS_KEY); return INTENSITY_COUNTS[k] !== undefined ? k : "medium"; } catch { return "medium"; }
};
const INTERACTIVE_SEL = "button,a,[role='button'],[role='switch'],input,textarea,select,[data-testid]";

// Custom cyber-ring cursor — mounted at DashboardLayout root.
// ZERO useState → ZERO re-renders after mount → ZERO positional lag.
// Position is pushed via CSS custom property "--ring-t" so the hover-pulse
// CSS animation can reference it in keyframes and still track live position.
function SpaceAgeCursor() {
  const ringRef      = useRef(null);
  const dotRef       = useRef(null);
  const canvasRef    = useRef(null);
  const prevRef      = useRef({ x: -100, y: -100 });
  const colorRef     = useRef(CURSOR_COLORS[getStoredColor()]);
  const hotRef       = useRef(false);
  const particlesRef = useRef([]);
  const rafRef       = useRef(null);
  const bloomRafRef  = useRef(null);
  const intensityRef = useRef(getStoredIntensity());
  const isDesktop    = useRef(typeof window !== "undefined" && window.matchMedia("(pointer:fine)").matches);

  // ── One-time DOM colour + position seed after mount ───────────────────────
  useEffect(() => {
    const ring = ringRef.current;
    const dot  = dotRef.current;
    if (!ring || !dot) return;
    const c = colorRef.current;
    ring.style.setProperty("--ring-t", "translate3d(-100px,-100px,0) translate(-50%,-50%)");
    ring.style.transform = "var(--ring-t)"; // use custom prop so keyframes inherit live position
    ring.style.border    = `1.5px solid rgba(${c.r},${c.g},${c.b},0.9)`;
    ring.style.boxShadow = `0 0 12px rgba(${c.r},${c.g},${c.b},0.75), inset 0 0 6px rgba(${c.r},${c.g},${c.b},0.5)`;
    dot.style.transform  = "translate3d(-100px,-100px,0) translate(-50%,-50%)";
    dot.style.background = c.hex;
    dot.style.boxShadow  = `0 0 10px ${c.hex}`;
  }, []);

  // ── Canvas resize ──────────────────────────────────────────────────────────
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const resize = () => { canvas.width = window.innerWidth; canvas.height = window.innerHeight; };
    resize();
    window.addEventListener("resize", resize, { passive: true });
    return () => window.removeEventListener("resize", resize);
  }, []);

  // ── Particle RAF loop ──────────────────────────────────────────────────────
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const tick = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const now = performance.now();
      const alive = [];
      for (const p of particlesRef.current) {
        const age = (now - p.born) / p.life;
        if (age >= 1) continue;
        alive.push(p);
        p.x += p.vx;  p.y += p.vy;
        p.vx *= 0.92; p.vy *= 0.92;
        ctx.globalAlpha = Math.max(0, p.alpha * (1 - age));
        ctx.fillStyle   = p.color;
        ctx.shadowColor = p.color;
        ctx.shadowBlur  = p.bloom ? 8 : 3;
        ctx.beginPath();
        ctx.arc(p.x, p.y, Math.max(0.3, p.size * (1 - age * 0.4)), 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalAlpha = 1; ctx.shadowBlur = 0;
      particlesRef.current = alive;
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  // ── Color / intensity event listeners ─────────────────────────────────────
  useEffect(() => {
    const onColor = (e) => {
      const key = e.detail?.color;
      if (!CURSOR_COLORS[key]) return;
      const c = CURSOR_COLORS[key];
      colorRef.current = c;
      const ring = ringRef.current;
      const dot  = dotRef.current;
      if (ring) {
        ring.style.border    = `1.5px solid rgba(${c.r},${c.g},${c.b},0.9)`;
        ring.style.boxShadow = `0 0 12px rgba(${c.r},${c.g},${c.b},0.75), inset 0 0 6px rgba(${c.r},${c.g},${c.b},0.5)`;
        if (hotRef.current) ring.style.background = `rgba(${c.r},${c.g},${c.b},0.12)`;
      }
      if (dot) { dot.style.background = c.hex; dot.style.boxShadow = `0 0 10px ${c.hex}`; }
    };
    const onIntensity = (e) => {
      const key = e.detail?.intensity;
      if (INTENSITY_COUNTS[key] !== undefined) intensityRef.current = key;
    };
    window.addEventListener("rs:cursor:color-change",     onColor);
    window.addEventListener("rs:cursor:intensity-change", onIntensity);
    return () => {
      window.removeEventListener("rs:cursor:color-change",     onColor);
      window.removeEventListener("rs:cursor:intensity-change", onIntensity);
    };
  }, []);

  // ── Mouse / click handlers ─────────────────────────────────────────────────
  useEffect(() => {
    const onMove = (e) => {
      const x = e.clientX;
      const y = e.clientY;
      const prev = prevRef.current;
      prevRef.current = { x, y };
      // Push position into CSS custom property — keyframes inherit live value.
      const t = `translate3d(${x}px,${y}px,0) translate(-50%,-50%)`;
      ringRef.current?.style.setProperty("--ring-t", t);
      if (dotRef.current) dotRef.current.style.transform = t;
      // Comet trail — desktop only.
      if (!isDesktop.current) return;
      const dx = x - prev.x;
      const dy = y - prev.y;
      if (Math.sqrt(dx * dx + dy * dy) < 2) return;
      const c   = colorRef.current;
      const col = `rgba(${c.r},${c.g},${c.b},1)`;
      const lvl = intensityRef.current;
      const n   = INTENSITY_COUNTS[lvl] ?? 7;
      // Intense → visually larger + brighter particles, not just more of them.
      const sizeBase  = lvl === "light" ? 0.3 : lvl === "intense" ? 1.6 : 0.5;
      const sizeRange = lvl === "light" ? 1.2 : lvl === "intense" ? 3.2 : 2.2;
      const alphaBase = lvl === "light" ? 0.25 : lvl === "intense" ? 0.70 : 0.45;
      const alphaRange= lvl === "light" ? 0.25 : lvl === "intense" ? 0.30 : 0.45;
      const lifeBase  = lvl === "intense" ? 130 : 100;
      const lifeRange = lvl === "intense" ? 120 : 100;
      for (let i = 0; i < n; i++) {
        const t2 = Math.random();
        particlesRef.current.push({
          x: x - dx * t2 + (Math.random() - 0.5) * 2,
          y: y - dy * t2 + (Math.random() - 0.5) * 2,
          vx: (Math.random() - 0.5) * 0.9, vy: (Math.random() - 0.5) * 0.9,
          size: Math.random() * sizeRange + sizeBase,
          alpha: alphaBase + Math.random() * alphaRange,
          color: col, born: performance.now(),
          life: lifeBase + Math.random() * lifeRange, bloom: false,
        });
      }
      if (particlesRef.current.length > 600) particlesRef.current = particlesRef.current.slice(-500);
    };

    const onOver = (e) => {
      const interactive = e.target?.closest?.(INTERACTIVE_SEL) != null;
      if (interactive === hotRef.current) return; // no change — skip DOM write
      hotRef.current = interactive;
      const ring = ringRef.current;
      if (!ring) return;
      const c = colorRef.current;
      ring.style.width      = interactive ? "40px" : "22px";
      ring.style.height     = interactive ? "40px" : "22px";
      ring.style.background = interactive ? `rgba(${c.r},${c.g},${c.b},0.12)` : "transparent";
      if (interactive) {
        // Restart pulse animation every time we enter a new interactive element.
        ring.classList.remove("cursor-ring-pulse");
        void ring.offsetWidth; // micro-reflow to re-trigger CSS animation
        ring.classList.add("cursor-ring-pulse");
      } else {
        ring.classList.remove("cursor-ring-pulse");
      }
    };

    const onClick = (e) => {
      if (!isDesktop.current) return;
      const x = e.clientX; const y = e.clientY;
      const c = colorRef.current;
      const col = `rgba(${c.r},${c.g},${c.b},1)`;
      // Canvas bloom particles
      for (let i = 0; i < 24; i++) {
        const angle = (i / 24) * Math.PI * 2;
        const speed = 2.5 + Math.random() * 4.5;
        particlesRef.current.push({
          x, y, vx: Math.cos(angle) * speed, vy: Math.sin(angle) * speed,
          size: Math.random() * 3.5 + 1, alpha: 0.9, color: col,
          born: performance.now(), life: 210 + Math.random() * 80, bloom: true,
        });
      }
      // Ring glow bloom — JS RAF animates boxShadow independently of the
      // CSS pulse animation (which owns transform), so they never conflict.
      const ring = ringRef.current;
      if (ring) {
        cancelAnimationFrame(bloomRafRef.current);
        const start = performance.now();
        const dur = 280;
        const tick = (now) => {
          const t = Math.min((now - start) / dur, 1);
          // Rise to peak at 20%, ease back to base.
          const phase = t < 0.2 ? t / 0.2 : 1 - (t - 0.2) / 0.8;
          const cc = colorRef.current; // pick up live color each frame
          const spread  = (phase * 4).toFixed(1);
          const glow1   = (12 + phase * 36).toFixed(0);
          const glow2   = (phase * 88).toFixed(0);
          ring.style.boxShadow = [
            `0 0 0 ${spread}px rgba(${cc.r},${cc.g},${cc.b},${(phase * 0.5).toFixed(2)})`,
            `0 0 ${glow1}px rgba(${cc.r},${cc.g},${cc.b},${(0.75 + phase * 0.25).toFixed(2)})`,
            `0 0 ${glow2}px rgba(${cc.r},${cc.g},${cc.b},${(phase * 0.35).toFixed(2)})`,
            `inset 0 0 6px rgba(${cc.r},${cc.g},${cc.b},0.5)`,
          ].join(", ");
          if (t < 1) {
            bloomRafRef.current = requestAnimationFrame(tick);
          } else {
            ring.style.boxShadow = `0 0 12px rgba(${cc.r},${cc.g},${cc.b},0.75), inset 0 0 6px rgba(${cc.r},${cc.g},${cc.b},0.5)`;
          }
        };
        bloomRafRef.current = requestAnimationFrame(tick);
      }
    };

    document.body.style.cursor = "none";
    document.addEventListener("mousemove", onMove, { passive: true });
    document.addEventListener("mouseover", onOver);
    window.addEventListener("click", onClick);
    return () => {
      document.body.style.cursor = "";
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseover", onOver);
      window.removeEventListener("click", onClick);
      cancelAnimationFrame(bloomRafRef.current);
    };
  }, []);

  // Static render — this component NEVER re-renders after mount.
  // All visual updates flow through DOM refs above.
  return (
    <>
      {/* Comet trail + click bloom canvas */}
      <canvas ref={canvasRef} style={{ position:"fixed", left:0, top:0, width:"100vw", height:"100vh", pointerEvents:"none", zIndex:99998 }} />
      {/* Ring — transition only size/bg; transform is NEVER transitioned */}
      <div
        ref={ringRef}
        data-testid="space-age-cursor-ring"
        style={{
          position:"fixed", left:0, top:0,
          width:"22px", height:"22px",
          willChange:"transform",
          borderRadius:"50%",
          pointerEvents:"none",
          zIndex:99999,
          transition:"width 140ms ease, height 140ms ease, background 140ms ease",
          background:"transparent",
        }}
      />
      {/* Dot — pure GPU composite, no transitions */}
      <div
        ref={dotRef}
        style={{
          position:"fixed", left:0, top:0,
          width:"4px", height:"4px",
          willChange:"transform",
          borderRadius:"50%",
          pointerEvents:"none",
          zIndex:99999,
        }}
      />
    </>
  );
}

export { CURSOR_COLORS, COLOR_LS_KEY, INTENSITY_COUNTS, INTENSITY_LS_KEY, PRISM_SPEEDS, PRISM_SPEED_LS_KEY, PRISM_ENGINE_ACTIVE_KEY, METEOR_INTENSITIES, METEOR_INTENSITY_LS_KEY };

export function SpaceAgeCursorMount() {
  const { user } = useAuth();
  if (!user?.space_age_deck_enabled || !user?.space_age_cursor_enabled) return null;
  return <SpaceAgeCursor />;
}

// Combined guard — hides the whole deck when the toggle is off.
export function SpaceAgeControlDeck({ children }) {
  const { user } = useAuth();
  const enabled = !!user?.space_age_deck_enabled;
  if (!enabled) return null;
  return children;
}

export default SpaceAgeControlDeck;
